
import { PropertyData, VurdData } from "./PropertyInfoCollectorTypes";
import type { EjendomSummariskPdfRequest } from "src/managers/TingbogDownload";
import { AppMessagesContext, Localization, SessionContext, SettingsManager, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import {
  EmptySpace,
  GlassFoldUdBox,
  GlassFoldUdBoxFalse,
  KeyValueLine,
  KeyValueTable,
  KeyValuesBoxGrid,
  PropertyMapContainer,
  RenderLinkToCompanyInfo,
  RenderLinkToCondominium,
  RenderLinkToPersonInfo,
  TooltipWrapper,
  TitleValueLine,
  ValueGroup,
  ViewButtonIcon

} from "./PropertyComponents";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import {
  ApplicationStateContext,
  Feature,
  FeatureType,
} from "src/states/ApplicationState";
import { Col, FloatingLabel, Form, Spinner } from "react-bootstrap";

import './PropertyInfoGroupFormatters.css';
import {
  plandataKeyParser, plandataParser,
  vurdBenytParser,
  vurdFordelingskode
} from "./PropertyInfoParser";
import { BBRTranslateByDataKey } from "src/managers/BBRTranslateByDataKey";
import { ExportData } from "src/managers/ExportData";
import { BBRKodeLister } from "./BBRKodeLister";
import { GlassButton, GlassButtonDropdown } from "src/components/GlassButtons";
import { EmbeddedVectorMapEnhanced } from "src/compentsModus/EmbeddedVectorMapEnhanced";
import { eMapLayerFill, eMapLayerOutline, eMapLayerTextSymbol, eMapSource } from "src/managers/EMapHelpers";
import { bbox, featureCollection, union } from "@turf/turf";
import { MarkData } from "src/components/MarkData";
import { EnergyLabelIcon, EnergyLabelPdfButton, filterLatestEnergyLabels } from "src/components/EnergyLabelComponents";
import { DocumentsComponent } from "src/components/DocumentsComponent";
import { PaddedContainer } from "src/components/MitGlassComponents";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { Mat_Jordstykke, Maybe, Bbr_Bygning, Bbr_Opgang, Bbr_Enhed, Bbr_TekniskAnlaeg } from "src/dataCollector/DFQGLTypes";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { formatDate } from "src/functions/FormatDate";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { FormatUtils } from "src/managers/FormatUtils";
import { createANNLLMAnalyzer, getServitutPdfFetchAktId } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { getServitutAktSearchTargets } from "src/modules/ANNModule/servitutAktSearch";
import { ServitutAnnotationCountBadge, ServitutAnnotationQuickDownloadButton } from "src/modules/ANNModule/ServitutAnnotationCountBadge";
import { buildServitutDetailsParams, normalizeServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import { AnnotationContextType } from "src/modules/ANNModule/ANNTypes";
import {
  TINGBOG_SCANNED_AKT_PREFIX,
  TingbogDownloadTarget,
  downloadEjendomSummariskPdf,
  downloadTingbogDocument,
  filterCurrentJordstykkerForEjendomSummarisk,
  toEjendomSummariskMatrikelListe,
  fetchTingbogDocumentXML,
  fetchTingbogDokumentPdfBlob,
  fetchTingbogPdfBlob,
  getTingbogDownloadFileName,
  getTingbogDownloadTarget,
  getTingbogPdfUrl,
  isTingbogElectronicDocument,
  shouldRenderTingbogDownloadButton,
} from "src/managers/TingbogDownload";
import {
  getServitutDownloadFileName,
  getTingbogAttestDownloadFileName,
} from "src/managers/ServitutDownloadNaming";
import { message } from "src/components/Message";
import { listPropertyTingbogs } from "src/dataCollector/parseTinglysningsDataXml";

function isTingbogUnavailable(data: Partial<PropertyData>): boolean {
  const tingbog = data.ViamapTingBog as unknown;
  return Boolean(tingbog) && (
    Array.isArray(tingbog) ||
    typeof tingbog !== "object" ||
    !("stamOplysninger" in tingbog!)
  );
}

function getTingbogUnavailableText(): string {
  return Localization.getText("Cannot contact Tinglysning right now. Please try again later.");
}

export class PropInfoGroupFormatters {
  static MatrikelKort(MatUdsnit: any): any {
    return (
      <>
        {(MatUdsnit).oversigtskort ? <img style={{ width: "100%" }} src={("oversigtskort" in (MatUdsnit) && "data:image/svg+xml;base64," + (MatUdsnit).oversigtskort) || ""} ></img> : <></>}
        {(MatUdsnit.matrikelklynger ?? []).map((a, idx) => {
          return (<GlassFoldUdBox key={a.matrikler[0].matrnr} title={"Område " + (idx + 1)} foldetUd={false} >
            <img style={{ width: "100%" }} src={("oversigtskort" in (MatUdsnit) && "data:image/svg+xml;base64," + (a).b64_svgz) || ""} ></img>
          </GlassFoldUdBox>)
        })}
      </>
    )
  }

  static BBRKort(BBRUdsnit: any): any {
    return (
      <>
        {(BBRUdsnit.matrikelklynger ?? []).map((a, idx) => {
          return (<GlassFoldUdBox key={idx} title={"Område " + (idx + 1)} foldetUd={idx == 0} >
            <img style={{ width: "100%" }} src={("data:image/svg+xml;base64," + (a)) || ""} ></img>
          </GlassFoldUdBox>)
        })}
      </>
    )

  }
  static ViamapMarkedsStatistik(data: PropertyData): any {
    if (data.ViamapEjendomStats) {
      let shortenArray = (val: any, idx: number) => {
        return idx > 3;
      };
      return (
        <>
          <KeyValueTable>
            <tr>
              <th>Kvartal:</th>
              {data.ViamapEjendomStats?.perioder
                ?.filter(shortenArray)
                .map((per, idx) => {
                  return <th key={idx}>{per}</th>;
                })}
            </tr>
            <tr>
              <td>
                {data.ViamapEjendomData?.kommuneNavn +
                  " (" +
                  (data.ViamapEjendomData?.kommuneNr || "") +
                  ")"}
              </td>
              {data.ViamapEjendomStats?.vaerdierBM010
                ?.filter(shortenArray)
                .map((per, idx) => {
                  let val = isNaN(per as any)
                    ? "-"
                    : new Intl.NumberFormat("da-dk", {
                      maximumFractionDigits: 0,
                    }).format(parseFloat(per.replace(".", "")));
                  return <td key={idx}>{val}</td>;
                })}
            </tr>
            <tr>
              <td>
                {(() => {
                  // Try GQL_EBRBFE first, then fall back to EBRBFE
                  const gqlHusnummer = data.GQL_EBRBFE?.EBR_Ejendomsbeliggenhed?.nodes?.[0]?.ejendomsbeliggenhedHarSomBeliggenhedsadresseHusnummer;
                  const gqlPostnummer = gqlHusnummer?.husnummerHoererTilIPostnummer;
                  if (gqlPostnummer) {
                    return (gqlPostnummer.postnr || "") + " " + (gqlPostnummer.navn || "");
                  }
                })()}
              </td>
              {data.ViamapEjendomStats?.vaerdierBM011
                ?.filter(shortenArray)
                .map((per, idx) => {
                  let val = isNaN(per as any)
                    ? "-"
                    : new Intl.NumberFormat("da-dk", {
                      maximumFractionDigits: 0,
                    }).format(parseFloat(per.replace(".", "")));
                  return <td key={idx}>{val}</td>;
                })}
            </tr>
          </KeyValueTable>
          <EmptySpace />
          <KeyValueLine
            valKey={`Kilde: ${data.ViamapEjendomStats?.kilde}`}
            value={""}
            keepEmpty
          />
          <KeyValueLine
            valKey={`Enhed: ${data.ViamapEjendomStats?.enhed}`}
            value={""}
            keepEmpty
          />
        </>
      );
    }
  }


  static PlandataDatoViser(plan: {}) {
    return Object.keys(plan).filter((a: string) => a.toLowerCase().includes("dato") && plan[a]).sort((a, b) => {
      let resA = parseInt(plan[a].toString().split("T")?.[0].replaceAll("-", ""));
      let resB = parseInt(plan[b].toString().split("T")?.[0].replaceAll("-", ""));
      return resA - resB
    }).map((a) => {
      return (
        <KeyValueLine
          CParser={plandataParser}
          KeyEndParser={plandataKeyParser}
          key={a}
          valKey={a}
          value={plan[a]}
        />
      )
    })
  }

  static Delområde(data: PropertyData, foldetUd: boolean) {
    if (data.PlanDataDelområde?.type !== "FeatureCollection") {
      return <GlassFoldUdBoxFalse group="Planer" title={"❗Kunne ikke hente delområde"} boxKey={"Delområder"} />
    }
    if (data.PlanDataDelområde?.features.length == 0) {
      return <GlassFoldUdBoxFalse group="Planer" title={"❓Intet delområde her"} boxKey={"Delområder"} />
    }
    return data?.PlanDataDelområde?.features.map((feature) => {
      let plan = feature.properties;
      return (
        <GlassFoldUdBox
          key={plan.lp_plannr + plan.delnr}
          boxKey={"Delområder"}
          group="Planer"
          title={`Delområde Plan - ${plan.lp_plannr} ${plan.delnr}`}
          foldetUd={foldetUd || false}

        >
          {Object.keys(plan).filter((a) => !a.includes("dato")).map((planKey) =>
            <KeyValueLine
              CParser={plandataParser}
              KeyEndParser={plandataKeyParser}
              key={planKey}
              valKey={planKey}
              value={plan[planKey]}
            />
          )}
          <EmptySpace />
          <ValueGroup>
            <TitleValueLine keepEmpty valKey={"Tidlinje"} value={""} />
            <KeyValuesBoxGrid minWidth="150px">
              {this.PlandataDatoViser(plan)}
            </KeyValuesBoxGrid>
          </ValueGroup>
        </GlassFoldUdBox>
      )
    })
  }

  static LokalPlan(data: PropertyData, foldetUd: boolean) {
    if (data.PlanDataLokalPlan?.type !== "FeatureCollection") {
      return <GlassFoldUdBoxFalse group="Planer" title={"❗Kunne ikke hente lokalplan"} boxKey={"Lokalplaner"} />
    }
    if (data.PlanDataLokalPlan?.features.length == 0) {
      return <GlassFoldUdBoxFalse group="Planer" title={"❓Ingen lokalplan her"} boxKey={"Lokalplaner"} />
    }



    return data?.PlanDataLokalPlan?.features.map((feature) => {
      let plan = feature.properties;
      return (
        <GlassFoldUdBox
          key={plan.id}
          boxKey={"Lokalplaner"}
          group="Planer"
          title={`Lokal Plan - ${plan.plannavn}`}
          foldetUd={foldetUd || false}

        >
          {Object.keys(plan).filter((a) => !a.includes("dato")).map((planKey) =>
            <KeyValueLine
              CParser={plandataParser}
              KeyEndParser={plandataKeyParser}
              key={planKey}
              valKey={planKey}
              value={plan[planKey]}
            />
          )}
          <EmptySpace />
          <ValueGroup>
            <TitleValueLine keepEmpty valKey={"Tidlinje"} value={""} />
            <KeyValuesBoxGrid minWidth="150px">
              {this.PlandataDatoViser(plan)}
            </KeyValuesBoxGrid>
          </ValueGroup>
        </GlassFoldUdBox>
      )
    })
  }

  static KommunePlan(data: PropertyData, foldetUd: boolean) {
    if (data.PlanDataKommunePlan?.type !== "FeatureCollection") {
      return <GlassFoldUdBoxFalse group="Planer" title={"❗Kunne ikke hente kommuneplan"} boxKey={"Kommuneplan"} />
    }
    if (data.PlanDataKommunePlan?.features.length == 0) {
      return <GlassFoldUdBoxFalse group="Planer" title={"❓Ingen kommuneplan her"} boxKey={"Kommuneplan"} />
    }

    return data?.PlanDataKommunePlan?.features.map((feature) => {
      let plan = feature.properties;
      return (
        <GlassFoldUdBox
          key={plan.id}
          group="Planer"
          title={`Kommune Plan - ${plan.plannavn}`}
          boxKey={"Kommuneplan"}
          foldetUd={foldetUd || false}
        >
          {Object.keys(plan).filter((a) => !a.includes("dato")).map((planKey) =>
            <KeyValueLine
              CParser={plandataParser}
              KeyEndParser={plandataKeyParser}
              key={planKey}
              valKey={planKey}
              value={plan[planKey]}
            />
          )}
          <EmptySpace />
          <ValueGroup>
            <TitleValueLine keepEmpty valKey={"Tidlinje"} value={""} />
            <KeyValuesBoxGrid minWidth="200px">
              {this.PlandataDatoViser(plan)}
            </KeyValuesBoxGrid>
          </ValueGroup>
        </GlassFoldUdBox>
      )
    })
  }

  static PlanDataKommuneAflyst(data: PropertyData) {
    if (data.PlanDataKommunePlanAflyst?.type !== "FeatureCollection") {
      return <GlassFoldUdBoxFalse group="Planer" title={"❗Kunne ikke hente aflyste kommuneplaner"} boxKey={"Aflyste Kommuneplaner"} />
    }
    if (data.PlanDataKommunePlanAflyst?.features.length == 0) {
      return <GlassFoldUdBoxFalse group="Planer" title={"❓Ingen aflyste kommuneplaner her"} boxKey={"Aflyste Kommuneplaner"} />
    }

    return (
      <GlassFoldUdBox group="Planer" title={"Aflyste Kommuneplaner"} boxKey={"Aflyste Kommuneplaner"}
        foldetUd={false} >
        {
          data.PlanDataKommunePlanAflyst?.features.toSorted((b, a) => a.properties.datoaflyst - b.properties.datoaflyst).map((a) => {
            let plan = a.properties;
            return (
              <ValueGroup key={plan.oid}>
                <TitleValueLine valKey={[plan.plannavn]} value={<a
                  type="application/pdf"
                  target="_blank"
                  href={plan.doklink}
                >Hent Dokument</a>} keepEmpty />
                <KeyValueLine valKey={"Hændelse"} value={plan.aflystype} />
                <KeyValueLine valKey={"Aflyst d."} value={plan.datoaflyst} CParser={(a, b) => FormatUtils.formatNumberStringDate(b)} />
              </ValueGroup>
            )
          })
        }

      </GlassFoldUdBox>
    )
  }

  static PlanDataLokalPlanAflyst(data: PropertyData) {
    if (data.PlanDataLokalPlanAflyst?.type !== "FeatureCollection") {
      return <GlassFoldUdBoxFalse group="Planer" title={"❗Kunne ikke hente aflyste lokalplaner"} boxKey={"Aflyste Lokalplaner"} />
    }
    if (data.PlanDataLokalPlanAflyst?.features.length == 0) {
      return <GlassFoldUdBoxFalse group="Planer" title={"❓Ingen aflyste lokalplaner her"} boxKey={"Aflyste Lokalplaner"} />
    }

    return (
      <GlassFoldUdBox group="Planer" title={"Aflyste LokalPlaner"} boxKey={"Aflyste Lokalplaner"}
        foldetUd={false} >
        {
          data.PlanDataLokalPlanAflyst?.features?.toSorted((b, a) => a.properties.datoaflyst - b.properties.datoaflyst).map((a, idx) => {
            let plan = a.properties
            return (
              <ValueGroup key={plan.oid + idx}>
                <TitleValueLine valKey={[plan.plannavn]} value={<a
                  type="application/pdf"
                  target="_blank"
                  href={plan.doklink}
                >Hent Dokument</a>} keepEmpty />
                <KeyValueLine valKey={"Hændelse"} value={plan.aflystype} />
                <KeyValueLine valKey={"Aflyst d."} value={plan.datoaflyst} CParser={(a, b) => FormatUtils.formatNumberStringDate(b)} />
              </ValueGroup>
            )
          })
        }

      </GlassFoldUdBox>
    )
  }

  static Vurderinger(data: PropertyData, foldetUd: boolean) {
    function VurdSort<T extends VurdData[number]>(a: T, b: T) {
      return a.år && b.år && a.år !== b.år
        ? a.år > b.år
          ? -1
          : 1
        : a.VurderetUnder || b.VurderetUnder
          ? a.VurderetUnder || false
            ? 1
            : -1
          : (a.ændringDato || 0) < (b.ændringDato || 0)
            ? 1
            : -1;
    }
    return data.Vurd?.sort(VurdSort).map((a, idx) => {
      return (
        <GlassFoldUdBox
          key={a.id}
          group="Vurderinger"
          boxKey={"Vurd " + a.år as "Vurd 2020" | "Vurd 2019" | "Vurd 2018" | "Vurd 2017" | "Vurd 2012"}
          title={
            <>
              {"Vurderingår " + a.år}{" "}
              <span style={{ fontSize: "0.7em" }}>
                (
                {PropertyInformation.VurdÆndringskode(
                  a.ændringKode || "",
                  a.år || 2000
                ) + (a.VurderetUnder ? " - Vurderet Under" : "")}
                )
              </span>
            </>
          }
          foldetUd={foldetUd && idx == 0}
        >
          {Object.keys(a)
            .filter(
              (key) =>
                ![
                  "id",
                  "dækningsafgiftForskelVærdi",
                  "dækningsafgiftPligtKode",
                  "dækningsafgiftPligtTypeKode",
                  "juridiskKategoriKode",
                  "juridiskKategoriTekst",
                  "juridiskUnderkategoriKode",
                  "juridiskUnderkategoriTekst",
                  "VURMark",
                  "Vurderingsejendom",
                ].includes(key)
            )
            .map((key) => {
              if (key === "benyttelseKode") {
                return <KeyValueLine key={key} valKey={key} value={a[key]} CParser={vurdBenytParser} />
              }
              return typeof a[key] !== "object" ? (
                <KeyValueLine key={key} valKey={key} value={a[key]} />
              ) : null;
            })}
          {(a.BFEnummerList?.length || 0) < 4 ? (
            a.BFEnummerList?.map((a, idx) => (
              <KeyValueLine
                key={a}
                valKey={idx != 0 ? "" : "Bfe Numre"}
                value={a}
              />
            ))
          ) : (
            <GlassFoldUdBox title={"Bfe Numre"} foldetUd={false}>
              <KeyValuesBoxGrid minWidth="150px">
                {a.BFEnummerList?.map((a) => (
                  <KeyValueLine
                    key={a}
                    valKey={a.toString()}
                    value={""}
                    keepEmpty={true}
                  />
                )) || <></>}
              </KeyValuesBoxGrid>
            </GlassFoldUdBox>
          )}
          {Object.keys(a)
            .filter(
              (key) => !["BFEnummerList", "Vurderingsejendom"].includes(key)
            )
            .map((key) => {
              return typeof a[key] == "object" ? (
                Array.isArray(a[key]) ? (
                  a[key].map((a, idx) => {
                    let title = key + " " + (a.tekst ?? a.kode ?? idx);
                    if (key == "Fordeling") {
                      title = vurdFordelingskode(a.ejerboligværdiKode);
                    }
                    return (
                      <GlassFoldUdBox
                        key={a + idx}
                        title={title}
                        foldetUd={false}
                      >
                        {Object.keys(a).map((key) => (
                          <KeyValueLine key={key} valKey={key} value={a[key]} />
                        ))}
                      </GlassFoldUdBox>
                    );
                  })
                ) : (
                  <GlassFoldUdBox key={key} title={key} foldetUd={false}>
                    {Object.keys(a[key]).map((nKey) => (
                      <KeyValueLine
                        key={nKey}
                        valKey={nKey}
                        value={a[key][nKey]}
                      />
                    ))}
                  </GlassFoldUdBox>
                )
              ) : null;
            }) || <></>}
        </GlassFoldUdBox>
      );
    });
  }

  static extractProperties(obj) {
    return obj?.features?.[0]?.properties;
  }

  static ArealerGroup(data: PropertyData) {
    let ejd = data.ViamapEjendomData;
    if (!ejd) {
      return [];
    }




    let sfe = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
    let jordstykker = (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || []).filter((a) => a.status === "Gældende" && a.samletFastEjendomLokalId === sfe?.id_lokalId) || [];
    let TAreal = jordstykker?.reduce((a, b: Mat_Jordstykke) => a + b.registreretAreal || 0, 0)
    let VAreal = jordstykker?.reduce((a, b: Mat_Jordstykke) => a + b.vejareal || 0, 0)
    let EAreal = ((TAreal || 0) - (VAreal || 0));

    let AreaFormatter = new Intl.NumberFormat("da-dk");

    let grunde = data.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer_18_Ejendomsrelation_bfeNummer_ref?.nodes?.[0]?.grundSamletFastEjendom?.nodes;
    // let EJLRelation =  .BFEnummer_18_Ejendomsrelation_bfeNummer_ref?.nodes;
    let bygninger = grunde?.map((a) => a.bygningGrund?.nodes || []).flat();
    let nuvaerendeBygninger = PropertyInformation.beregnSamletBebyggetAreal(bygninger);
    let type = PropertyInformation.beregnBebyggelsesType(bygninger);
    const samletBygningsareal = PropertyInformation.beregnSamletBygningsAreal(bygninger)
    return [
      { key: "Bebyggelsestype", name: "Bebyggelsestype", val: type },
      {
        key: "Total-/grundareal",
        name: "Total-/grundareal",
        val: AreaFormatter.format(TAreal),
      },
      {
        key: "Heraf vej",
        name: "Heraf vej",
        val: AreaFormatter.format(VAreal),
      },
      {
        key: "Ekskl. vej",
        name: "Ekskl. vej",
        val: AreaFormatter.format(EAreal),
      },
      {
        key: "Samlet Bebygget areal",
        name: "Samlet Bebygget areal",
        val: AreaFormatter.format(nuvaerendeBygninger),
      },
      {
        key: "Samlet Bygningsareal",
        name: "Samlet Bygningsareal",
        val: AreaFormatter.format(samletBygningsareal),
        infoText: "Excl. kælder og tagetage"
      }
      // {
      //   key: "Max bebyggelsespct",
      //   name: "Max bebyggelsespct",
      //   val: formatRule(regler.bebygpct),
      // },
      // {
      //   key: "Max byggehøjde",
      //   name: "Max byggehøjde",
      //   val: formatRule(regler.maxbygnhjd),
      // },
      // {
      //   key: "Max etager",
      //   name: "Max etager",
      //   val: formatRule(regler.maxetager),
      // },
      // {
      //   key: "Restbyggeareal",
      //   name: "Restbyggeareal",
      //   val: AreaFormatter.format(restByggeareal),
      // },
    ];
  }

  static TingbogEjendom(
    data: PropertyData,
    access: (feature: FeatureType) => boolean,
    tingbogAttestCadastralHints?: TingbogAttestCadastralHints,
  ): any {
    let record = data.ViamapTingBog?.stamOplysninger?.indskannetAktDokumentId;
    const typeDisplay = data.ViamapTingBog?.stamOplysninger?.ejendomTypeDisplay
      || data.ViamapTingBog?.stamOplysninger?.ejendomType;
    const fordeling = data.ViamapTingBog?.stamOplysninger?.fordelingstal;
    const fordelingText = fordeling?.taeller && fordeling?.naevner
      ? `${fordeling.taeller}/${fordeling.naevner}`
      : "";
    const identityRows = (
      <>
        {typeDisplay ? <KeyValueLine valKey="Type" value={typeDisplay} /> : null}
        {fordelingText ? <KeyValueLine valKey="Fordelingstal" value={fordelingText} /> : null}
      </>
    );
    const propertyAnnotationId = getPropertyAnnotationIdentifier(data);
    const propertyAktActionAvailability = getTingbogPdfActionAvailability({
      downloadTarget: record && access(Feature.DownloadAktFraTinglysning)
        ? { kind: "akt", value: record }
        : undefined,
      viewerAktId: record,
      viewerContextType: AnnotationContextType.Ejendom,
      viewerBfeNumber: propertyAnnotationId,
    }, access);
    const attestRow = (
      <TingbogsattestDownloadRow
        data={data}
        access={access}
        cadastralHints={tingbogAttestCadastralHints}
      />
    );
    if (isTingbogUnavailable(data)) {
      return (
        <>
          {attestRow}
          {identityRows}
          {getTingbogUnavailableText()}
        </>
      );
    }
    if (record)
      return (
        <>
          {attestRow}
          {identityRows}
          <KeyValueLine
            valKey="Indskannet Akt"
            value={
              <div style={{ display: "flex", alignItems: "center", gap: "0.5em", justifyContent: "flex-end", width: "100%" }}>
                <a
                  type="application/pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={TINGBOG_SCANNED_AKT_PREFIX + record}
                >
                  {record}
                </a>
                {propertyAktActionAvailability.hasAnyActions ? (
                  <TingbogPdfActions
                    downloadTarget={access(Feature.DownloadAktFraTinglysning)
                      ? { kind: "akt", value: record }
                      : undefined}
                    downloadFileName={getTingbogDownloadFileName(`akt_${record}`)}
                    viewerAktId={record}
                    viewerContextType={AnnotationContextType.Ejendom}
                    viewerBfeNumber={propertyAnnotationId}
                    viewerDocumentFilename={getTingbogDownloadFileName(`akt_${record}`)}
                  />
                ) : null}
              </div>
            }
          />
          {(data.ViamapTingBog?.stamOplysninger.tillaegsTekster || []).map(
            (tekst, idx) => {
              if (typeof tekst === "string") {
                return <KeyValueLine key={idx} valKey="Tillæg" value={tekst} />;
              }
              const addendumActionAvailability = getTingbogPdfActionAvailability({
                downloadTarget: access(Feature.DownloadAktFraTinglysning)
                  ? { kind: "akt", value: tekst.dokumentId }
                  : undefined,
                viewerAktId: tekst.dokumentId,
                viewerContextType: AnnotationContextType.Ejendom,
                viewerBfeNumber: propertyAnnotationId,
              }, access);
              return (
                <KeyValueLine
                  key={idx}
                  valKey={"Tillæg " + tekst.overskrift}
                  value={
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5em", justifyContent: "flex-end", width: "100%" }}>
                      <a
                        type="application/pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={TINGBOG_SCANNED_AKT_PREFIX + tekst.dokumentId}
                      >
                        {tekst.dokumentId}
                      </a>
                      {tekst.dokumentId && addendumActionAvailability.hasAnyActions ? (
                        <TingbogPdfActions
                          downloadTarget={access(Feature.DownloadAktFraTinglysning)
                            ? { kind: "akt", value: tekst.dokumentId }
                            : undefined}
                          downloadFileName={getTingbogDownloadFileName(`akt_${tekst.dokumentId}`)}
                          viewerAktId={tekst.dokumentId}
                          viewerContextType={AnnotationContextType.Ejendom}
                          viewerBfeNumber={propertyAnnotationId}
                          viewerDocumentFilename={getTingbogDownloadFileName(`akt_${tekst.dokumentId}`)}
                        />
                      ) : null}
                    </div>
                  }
                />
              );
            }
          )}
        </>
      );
    return (
      <>
        {attestRow}
        {identityRows}
        {Localization.getText("No data")}
      </>
    );
  }

  static EjerLejligheden(DarAdresse: any, MatEjerlejlighed: any): any {

    if (!MatEjerlejlighed) {
      return <></>
    }

    const Mat = MatEjerlejlighed

    return (
      <>
        <KeyValueLine valKey={"Adresse"} value={DarAdresse?.[0]?.adressebetegnelse} />
        <KeyValueLine valKey={"SFE Nummer "} value={Mat.samletFastEjendomBFEnummer} />
        <KeyValueLine valKey={"BFE Nummer "} value={Mat.BFEnummer} />
        <KeyValueLine valKey={"Ejerlejlighedsnummer "} value={Mat.ejerlejlighedsnummer} />
        <KeyValueLine valKey={"Type"} value={"Ejerlejlighed"} />
      </>
    )
  }

  static LejAreal(MatEjerlejlighed: any) {
    const Mat = MatEjerlejlighed
    return (
      <>
        {Mat ? <KeyValueLine valKey={"Samlet Areal"} value={Mat.samletAreal} /> : <></>}
        {Mat ? <KeyValueLine valKey={"Fordelingstal"} value={Number(Mat.fordelingstalTaeller / Mat.fordelingstalNaevner).toLocaleString(Localization.getLocale(), { style: 'percent', minimumFractionDigits: 2 })} /> : <></>}
        {Mat ? <KeyValueLine valKey={"Andele af Hovedejendom"} value={Mat.fordelingstalTaeller} /> : <></>}
        {Mat ? <KeyValueLine valKey={"Andele i Hovedejendom"} value={Mat.fordelingstalNaevner} /> : <></>}
      </>
    )
  }

  static LejHandel(ESEjerlejlighed: any) {
    const Es = ESEjerlejlighed;
    return (
      <>
        {Es.handel_tidspunkt ? <KeyValueLine valKey={"Virkning fra"} value={Es.handel_tidspunkt} /> : <></>}
        {Es.handel_forretningsh ? <KeyValueLine valKey={"Forretningshændelse"} value={Es.handel_forretningsh} /> : <></>}
        {Es.handel_maade ? <KeyValueLine valKey={"Overdragelsesmåde"} value={Es.handel_maade} /> : <></>}
        {Es.handel_samletKoebesum ? <KeyValueLine valKey={"SamletKoebesum"} value={Es.handel_samletKoebesum} /> : <></>}
        {Es.handel_status && Es.handel_status !== "gældende" ? <KeyValueLine valKey={"Status"} value={Es.handel_status} /> : <></>}
        {Es.handel_valutaKode ? <KeyValueLine valKey={"Valutakode"} value={Es.handel_valutaKode} /> : <></>}
      </>
    )
  }

  static EjerLejEjer(ESEjerlejlighed: any) {
    const Es = ESEjerlejlighed;

    return (
      <>
        {Es ? <KeyValueLine valKey={"Ejer"} value={Es.ejf_ejere} /> : <></>}
        {Es.ejf_cvrnr ? <KeyValueLine valKey={"CVRnummer"} KeyEndParser={(a) => a} value={<RenderLinkToCompanyInfo cvrNr={Es.ejf_cvrnr}>{Es.ejf_cvrnr}</RenderLinkToCompanyInfo>} /> : <></>}
      </>
    )
  }

  static EkstraEjerlejlighed(bfeNr: number | string, ESEjerlejlighed?: any[], Mat2Ejerlejlighed?: any[]) {
    if (!bfeNr) {
      return <></>
    }
    const Es = ESEjerlejlighed?.find((a) => a?.bfe_nr == bfeNr)
    const Mat = Mat2Ejerlejlighed?.find((a) => a?.BFEnummer == bfeNr)

    return (
      <>
        {Mat ?
          <>
            <KeyValueLine valKey={"Samlet Areal"} value={Mat.samletAreal} />
            <KeyValueLine valKey={"Ejerlejlighedsnummer "} value={Mat.ejerlejlighedsnummer} />
          </>
          : <> </>}
        {Es ? (
          <>
            <KeyValueLine _className="compact" valKey={"Adresse"} value={(Es.bfe_adresse || "").replace(/,([^,]*)$/, ",\n$1")} />
            <KeyValueLine valKey={"Ejer"} value={Es.ejf_ejere} />
            <KeyValueLine valKey={"Reklamebeskyttet"} value={Es.ejf_reklameBeskyttelse} />
            <KeyValueLine valKey={"Senest handelpris"} value={Es.handel_samletKoebesum} />
            <KeyValueLine valKey={"Handelstidspunkt"} value={Es.handel_tidspunkt} />
          </>
        ) : <></>}
      </>
    )

  }

  static BBRQGLTeknikGrund(data: PropertyData["GQL_BBR"]) {
    let EJLRelation = data?.MAT_SamletFastEjendom.nodes?.[0]?.BFEnummer_18_Ejendomsrelation_bfeNummer_ref?.nodes;
    let Grunde = EJLRelation?.[0]?.grundSamletFastEjendom?.nodes;

    if (!Grunde || Grunde.length == 0) {
      return <></>
    }
    return (
      <>
        {Grunde!.map((grund) => (
          <Fragment key={grund?.id_lokalId}>
            <PropInfoGroupFormatters.BBRHierarkiAnlaeg tekniskAnlaeg={grund?.grundTekniskAnlaeg?.nodes} />
          </Fragment>
        ))}
      </>
    )
  }

  static GQL_BFE(data: PropertyData["GQL_BFE"]) {
    const sfeNode = data?.MAT_SamletFastEjendom?.nodes?.[0];
    if (!sfeNode) {
      return [];
    }

    const jordstykker = sfeNode.jordstykkeSamlesISamletFastEjendom?.nodes || [];

    if (jordstykker.length === 0) {
      return [];
    }

    const matSort = (a: any, b: any) => {
      const parseEjerlav =
        parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10) -
        parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
      if (parseEjerlav !== 0) {
        return parseEjerlav;
      }
      return PropertyInformation.matrikelSortFunc(
        a.matrikelnummer || "",
        b.matrikelnummer || ""
      );
    };

    const filterGaeldende = (elm: any) => {
      return elm?.status === "Gældende";
    };

    const constructName = (jordstykke: any) => {
      const ejerlav = jordstykke.jordstykkeLiggerIEjerlav;
      const ejerlavskode = ejerlav?.ejerlavskode || "";
      const ejerlavsnavn = ejerlav?.ejerlavsnavn || "";

      if (jordstykke.faelleslod)
        return `${jordstykke.matrikelnummer} ${ejerlavsnavn} (${ejerlavskode}), Fælleslod`;
      return `${jordstykke.matrikelnummer} ${ejerlavsnavn} (${ejerlavskode})`;
    };

    // Return jordstykker with flattened properties for easier access
    return jordstykker
      .filter(filterGaeldende)
      .sort(matSort)
      .map((jordstykke) => {
        const ejerlav = jordstykke.jordstykkeLiggerIEjerlav;
        return {
          key: jordstykke.id_lokalId || constructName(jordstykke),
          name: constructName(jordstykke),
          jordstykke: {
            ...jordstykke,
            ejerlavskode: ejerlav?.ejerlavskode,
            ejerlavsnavn: ejerlav?.ejerlavsnavn,
            kommuneLokalId: jordstykke.jordstykkeLiggerIMatrikelKommune?.id_lokalId,
            regionLokalId: jordstykke.jordstykkeLiggerIMatrikelRegion?.id_lokalId,
            sognLokalId: jordstykke.jordstykkeLiggerIMatrikelSogn?.id_lokalId,
            centroide: jordstykke.centroideLiggerIndenForJordstykke?.nodes?.map((c: any) => ({
              geometri: c.geometri?.wkt,
              senesteSagLokalId: c.senesteSagLokalId,
              status: c.status
            })) || [],
          }
        };
      });
  }

  static BBRQGL(data: PropertyData["GQL_BBR"]) {
    let EJLRelation = data?.MAT_SamletFastEjendom.nodes?.[0]?.BFEnummer_18_Ejendomsrelation_bfeNummer_ref?.nodes;
    let Grunde = EJLRelation?.[0]?.grundSamletFastEjendom?.nodes;

    if ((EJLRelation?.length ?? 0) > 1) {
      console.warn(`Multiple (SFE: ${data?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer} ) EJLRelation exist, only first shown`);
    }
    let bfeNummmer = data?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer;

    if ((Grunde?.length ?? 0) == 0) {
      return <>Tes</>
    }

    return (
      <>
        <ValueGroup key={"links"}>
          <KeyValuesBoxGrid minWidth="150px">
            <KeyValueLine valKey={"Bygninger"} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.length ?? 0), 0).toLocaleString("da-dk")} keepEmpty />
            <KeyValueLine valKey={"Etager"} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning?.byg054AntalEtager ?? 0), 0) ?? 0), 0).toLocaleString("da-dk")} keepEmpty />
            <KeyValueLine valKey={"Enheder"} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning?.liggerIBygning?.nodes?.length ?? 0), 0) ?? 0), 0).toLocaleString("da-dk")} keepEmpty />
            <KeyValueLine valKey={"Bygning"} infoText="Samlet bygningsareal" KeyEndParser={(a) => a} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning.byg038SamletBygningsareal ?? 0), 0) ?? 0), 0).toLocaleString("da-dk") + " m²"} keepEmpty />
            <KeyValueLine valKey={"Bebygget"} infoText="Samlet bebygget areal" KeyEndParser={(a) => a} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning.byg041BebyggetAreal ?? 0), 0) ?? 0), 0).toLocaleString("da-dk") + " m²"} keepEmpty />
            <KeyValueLine valKey={"Beboelse"} infoText="Samlet beboelsesareal" KeyEndParser={(a) => a} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning?.byg039BygningensSamledeBoligAreal ?? 0), 0) ?? 0), 0).toLocaleString("da-dk") + " m²"} keepEmpty />
            <KeyValueLine valKey={"Erhverv"} infoText="Samlet erhvervsareal" KeyEndParser={(a) => a} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning?.byg040BygningensSamledeErhvervsAreal ?? 0), 0) ?? 0), 0).toLocaleString("da-dk") + " m²"} keepEmpty />
            <KeyValueLine valKey={"Andet"} infoText="Samlet andet areal" KeyEndParser={(a) => a} value={Grunde?.reduce((acc, grund) => acc + (grund?.bygningGrund?.nodes?.reduce((bAcc, bygning) => bAcc + (bygning?.byg048AndetAreal ?? 0), 0) ?? 0), 0).toLocaleString("da-dk") + " m²"} keepEmpty />

          </KeyValuesBoxGrid>
          <KeyValueLine
            key={"links"}
            valKey={""}
            value={
              <>
                <a style={{ marginInline: "1ex" }} target="_blank" href={"https://kort.bbr.dk/?p=715776.81,6177239.359,12&bfe=" + bfeNummmer} >
                  BBR-Kort
                </a>
                <a style={{ marginInline: "1ex" }} target="_blank" href={"https://bbr.dk/se-bbr-oplysninger/0/1/0/bfe:" + bfeNummmer} >
                  BBR-oplysninger
                </a>
                <a style={{ marginLeft: "1ex" }} target="_blank" href={"https://bbr.dk/pls/wwwdata/get_newois_pck.show_bbr_meddelelse_pdf?i_bfe=" + bfeNummmer} >
                  BBR-Meddelelse
                </a>
              </>
            }
          />
        </ValueGroup>
        {Grunde!.map((grund) => (
          <Fragment key={grund?.id_lokalId}>
            <PropInfoGroupFormatters.BBRHierarkiBygninger bygninger={grund?.bygningGrund?.nodes} />
          </Fragment>
        ))}
      </>
    )
  }

  static BBRHierarkiBygninger({ bygninger }: { bygninger?: Maybe<Bbr_Bygning[]> }) {
    if (!bygninger || bygninger.length == 0) {
      return <></>
    }

    return (
      <>
        {bygninger!.map((bygning) => (
          <ValueGroup key={bygning?.id_lokalId}>
            {Object.keys(bygning || {}).map((key) => {
              let valRes = PropInfoGroupFormatters.BBRKeyValParser(key, bygning[key as keyof Bbr_Bygning]);
              let keyRes = ExportData.BBRDefaultKeyTranslator(key);
              return <KeyValueLine key={key} KeyEndParser={(_) => keyRes} valKey={keyRes} value={valRes} />
            })}
            {bygning?.liggerIBygning?.nodes?.length ?
              <GlassFoldUdBox title={"Enheder"} foldetUd={false}>
                <PropInfoGroupFormatters.BBRHierarkiEnheder enheder={bygning?.liggerIBygning?.nodes} />
              </GlassFoldUdBox>
              : <></>}
          </ValueGroup>
        ))}
      </>
    )
  }

  static BBRHierarkiOpgange({ opgange }: { opgange?: Maybe<Bbr_Opgang[]> }) {
    if (!opgange || opgange.length == 0) {
      return <></>
    }
    return (
      <>
        {opgange!.map((opgang, idx) => (
          <GlassFoldUdBox key={opgang?.id_lokalId} title={`Opgang BFE: ${idx + 1}`} foldetUd={false}>
            <PropInfoGroupFormatters.BBRHierarkiEnheder enheder={opgang?.enhedOpgang?.nodes} />
          </GlassFoldUdBox>
        ))}
      </>
    )
  }

  static BBRHierarkiEnheder({ enheder }: { enheder?: Maybe<Bbr_Enhed[]> }) {
    if (!enheder || enheder.length == 0) {
      return <></>
    }
    return (
      <>
        {enheder!.map((enhed, idx) => (
          <ValueGroup key={enhed?.id_lokalId}>
            <TitleValueLine valKey={`Enhed ${idx + 1}`} value={""} keepEmpty />
            {Object.keys(enhed || {}).map((key) => {
              let valRes = PropInfoGroupFormatters.BBRKeyValParser(key, enhed[key as keyof Bbr_Enhed]);
              let keyRes = ExportData.BBRDefaultKeyTranslator(key);
              return <KeyValueLine key={key} KeyEndParser={(_) => keyRes} valKey={keyRes} value={valRes} />
            }
            )}
          </ValueGroup>
        ))}
      </>
    )
  }

  static BBRHierarkiAnlaeg({ tekniskAnlaeg }: { tekniskAnlaeg?: Maybe<Bbr_TekniskAnlaeg[]> }) {
    if (!tekniskAnlaeg || tekniskAnlaeg.length == 0) {
      return <></>
    }

    return (
      <>
        {tekniskAnlaeg!.map((anlaeg) => (
          <ValueGroup key={anlaeg?.id_lokalId}>
            <TitleValueLine valKey={`Teknisk Anlæg ${anlaeg.tek007Anlaegsnummer}`} value={""} keepEmpty />
            {Object.keys(anlaeg || {}).map((key) => {
              let valRes = PropInfoGroupFormatters.BBRKeyValParser(key, anlaeg[key as keyof Bbr_TekniskAnlaeg]);
              let keyRes = ExportData.BBRDefaultKeyTranslator(key);
              return <KeyValueLine key={key} KeyEndParser={(_) => keyRes} valKey={keyRes} value={valRes} />
            })}
          </ValueGroup>
        ))}
      </>
    )
  }


  static SFE(data: PropertyData) {
    const filterGaeldende = (elm: any) => {
      return elm?.status === "Gældende" || elm?.properties?.status === "Gældende";
    }

    const prop = data.BBRSFE?.filter?.(filterGaeldende)?.[0]?.properties || null;

    if (!prop) {
      return <>No active SFE exist</>
    }


    return (
      <>
        <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
          <>
            {Object.keys(prop)
              .filter(
                (keys) =>
                  ![
                    "sekundaerForretningshaendelse",
                    "forretningsomraade",
                    "forretningsproces",
                    "id_lokalId",
                    "id_namespace",
                    "jordstykke",
                    "paataenktHandling",
                    "registreringTil",
                    "senesteSagLokalId",
                    "virkningTil",
                  ].includes(keys)
              )
              .map((key) => {
                return typeof prop[key] !== "object" ? (
                  <KeyValueLine key={key} valKey={key} value={prop[key]} />
                ) : null;
              })}
            <KeyValueLine
              valKey={"Antal Bygninger på Fremmed Grund"}
              value={(prop && prop?.["bygningPaaFremmedGrund"]?.filter(filterGaeldende)?.length) || 0}
            />
            <KeyValueLine
              valKey={"Antal Ejerlejligheder"}
              value={(prop && prop?.["ejerlejlighed"]?.filter(filterGaeldende)?.length) || 0}
            />
            <KeyValueLine
              valKey={"Antal Jordstykker"}
              value={(prop && prop?.["jordstykke"]?.filter(filterGaeldende)?.length) || 0}
            />
          </>
        </KeyValuesBoxGrid>
        {prop && prop?.["bygningPaaFremmedGrund"]?.filter(filterGaeldende)?.length ? (
          <GlassFoldUdBox title={"Bygninger på Fremmed Grund"} foldetUd={false}>
            <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
              {prop?.["bygningPaaFremmedGrund"]?.filter(filterGaeldende).map((a, idx) => {
                return (
                  <ValueGroup key={idx}>
                    {Object.keys(a).map((keys) => (
                      <KeyValueLine
                        key={keys}
                        valKey={keys}
                        value={a[keys]}
                      />
                    ))}
                  </ValueGroup>
                );
              })}
            </KeyValuesBoxGrid>
          </GlassFoldUdBox>
        ) : null}
        {prop && prop?.["ejerlejlighed"]?.filter(filterGaeldende)?.length ? (
          <GlassFoldUdBox title={"Ejerlejligheder"} foldetUd={false}>
            <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
              {prop?.["ejerlejlighed"]?.filter(filterGaeldende).map((a, idx) => {
                return (
                  <ValueGroup key={idx}>

                    {Object.keys(a).map((keys) => (
                      keys === "BFEnummer" ?
                        <KeyValueLine
                          key={keys}
                          valKey={keys}
                          value={<RenderLinkToCondominium bfeNr={a[keys]}>{a[keys]}</RenderLinkToCondominium>}
                        />
                        : <KeyValueLine
                          key={keys}
                          valKey={keys}
                          value={a[keys]}
                        />

                    ))}
                    {this.EkstraEjerlejlighed(a.BFEnummer, data.ESearchEjerlejlighed, data.Mat2Ejerlejlighed)}
                  </ValueGroup>
                );
              })}
            </KeyValuesBoxGrid>
          </GlassFoldUdBox>
        ) : null}
        {prop && prop?.["jordstykker"]?.length ? (
          <GlassFoldUdBox title={"Jordstykker"} foldetUd={false}>
            <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
              {prop?.["jordstykker"].map((a, idx) => {
                return (
                  <ValueGroup key={idx}>
                    {Object.keys(a).map((keys) => (
                      <KeyValueLine
                        key={keys}
                        valKey={keys}
                        value={prop[keys]}
                      />
                    ))}
                  </ValueGroup>
                );
              })}
            </KeyValuesBoxGrid>
          </GlassFoldUdBox>
        ) : null}
      </>
    );
  }

  static SFEGQL(data: PropertyData["GQL_BFE"], fullData: PropertyData) {
    const sfeNode = data?.MAT_SamletFastEjendom?.nodes?.[0];
    if (!sfeNode) {
      return <>No active SFE exist</>;
    }

    const bygningPaaFremmedGrundFlade = sfeNode.bygningPaaFremmedGrundFladeLiggerPaaSamletFastEjendom?.nodes || [];
    const bygningPaaFremmedGrundPunkt = sfeNode.bygningPaaFremmedGrundPunktLiggerPaaSamletFastEjendom?.nodes || [];
    const ejerlejlighederSFE = sfeNode.ejerlejlighedErDelAfSamletFastEjendom?.nodes || [];
    const jordstykker = sfeNode.jordstykkeSamlesISamletFastEjendom?.nodes || [];

    const filterGaeldende = (elm: any) => {
      return elm?.status === "Gældende";
    };

    // Collect ejerlejligheder from all sources
    const ejerlejlighederBPFGF = bygningPaaFremmedGrundFlade
      .filter(filterGaeldende)
      .flatMap((bpfgf) => bpfgf.ejerlejlighedErDelAfBygningPaaFremmedGrundFlade?.nodes || [])
      .filter(filterGaeldende);
    const ejerlejlighederBPFGP = bygningPaaFremmedGrundPunkt
      .filter(filterGaeldende)
      .flatMap((bpfgp) => bpfgp.ejerlejlighedErDelAfBygningPaaFremmedGrundPunkt?.nodes || [])
      .filter(filterGaeldende);

    // Combine all ejerlejligheder and deduplicate by BFEnummer
    const allEjerlejligheder = [
      ...ejerlejlighederSFE.filter(filterGaeldende),
      ...ejerlejlighederBPFGF,
      ...ejerlejlighederBPFGP
    ];
    const seenBFE = new Set<string | number>();
    const uniqueEjerlejligheder = allEjerlejligheder.filter((ejl) => {
      const bfe = ejl.BFEnummer;
      if (seenBFE.has(bfe)) {
        return false;
      }
      seenBFE.add(bfe);
      return true;
    });

    const bygningPaaFremmedGrund = [
      ...bygningPaaFremmedGrundFlade.filter(filterGaeldende),
      ...bygningPaaFremmedGrundPunkt.filter(filterGaeldende)
    ];

    const excludedKeys = [
      "sekundaerForretningshaendelse",
      "forretningsomraade",
      "forretningsproces",
      "id_lokalId",
      "id_namespace",
      "jordstykkeSamlesISamletFastEjendom",
      "bygningPaaFremmedGrundFladeLiggerPaaSamletFastEjendom",
      "bygningPaaFremmedGrundPunktLiggerPaaSamletFastEjendom",
      "ejerlejlighedErDelAfSamletFastEjendom",
      "paataenktHandling",
      "registreringTil",
      "senesteSagLokalId",
      "virkningTil",
    ];

    return (
      <>
        <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
          {Object.keys(sfeNode)
            .filter((key) => !excludedKeys.includes(key) && typeof sfeNode[key] !== "object" && sfeNode[key] != null)
            .map((key) => (
              <KeyValueLine key={key} valKey={key} value={sfeNode[key]} />
            ))}
          <KeyValueLine
            valKey={"Antal Bygninger på Fremmed Grund"}
            value={bygningPaaFremmedGrund.length}
          />
          <KeyValueLine
            valKey={"Antal Ejerlejligheder"}
            value={uniqueEjerlejligheder.length}
          />
          <KeyValueLine
            valKey={"Antal Jordstykker"}
            value={jordstykker.filter(filterGaeldende).length}
          />
        </KeyValuesBoxGrid>
        {bygningPaaFremmedGrund.length > 0 ? (
          <GlassFoldUdBox title={"Bygninger på Fremmed Grund"} foldetUd={false}>
            <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
              {bygningPaaFremmedGrund.map((a, idx) => {
                return (
                  <ValueGroup key={idx}>
                    {Object.keys(a)
                      .filter((key) => typeof a[key] !== "object" && a[key] != null)
                      .map((keys) => (
                        <KeyValueLine
                          key={keys}
                          valKey={keys}
                          value={a[keys]}
                        />
                      ))}
                  </ValueGroup>
                );
              })}
            </KeyValuesBoxGrid>
          </GlassFoldUdBox>
        ) : null}
        {uniqueEjerlejligheder.length > 0 ? (
          <GlassFoldUdBox title={"Ejerlejligheder"} foldetUd={false}>
            <KeyValuesBoxGrid minWidth="200px" maxCols={2}>
              {uniqueEjerlejligheder.map((a, idx) => {
                return (
                  <ValueGroup key={idx}>
                    {Object.keys(a)
                      .filter((key) => typeof a[key] !== "object" && a[key] != null)
                      .map((keys) => (
                        keys === "BFEnummer" ? (
                          <KeyValueLine
                            key={keys}
                            valKey={keys}
                            value={<RenderLinkToCondominium bfeNr={a[keys]}>{a[keys]}</RenderLinkToCondominium>}
                          />
                        ) : (
                          <KeyValueLine
                            key={keys}
                            valKey={keys}
                            value={a[keys]}
                          />
                        )
                      ))}
                    {this.EkstraEjerlejlighed(a.BFEnummer, fullData.ESearchEjerlejlighed, fullData.Mat2Ejerlejlighed)}
                  </ValueGroup>
                );
              })}
            </KeyValuesBoxGrid>
          </GlassFoldUdBox>
        ) : null}
      </>
    );
  }

  static TingbogHæftelser(
    data: PropertyData,
    access: (a: FeatureType) => boolean,
  ) {
    if (isTingbogUnavailable(data)) {
      return getTingbogUnavailableText();
    }

    const propertyAnnotationId = getPropertyAnnotationIdentifier(data);
    return (
      data.ViamapTingBog?.hæftelser?.liste?.map((hæftelse) => {
        let haeftelsePdfAction: React.ReactNode = null;
        if (
          ENABLE_TINGBOG_ADKOMST_HAEFTELSE_PDF_UI &&
          isTinglysningPdfEligibleByDate(hæftelse.tinglysningsDato)
        ) {
          const recordNo = TingBogRecordNumber(
            hæftelse.aktId || hæftelse.aktAliasId
          );
          const isDigitalizedHæftelse = isTingbogElectronicDocument(hæftelse);
          const hFilnavn = (hæftelse as { dokumentFilnavn?: string }).dokumentFilnavn?.trim();
          const viewerAktId = isDigitalizedHæftelse
            ? undefined
            : (hFilnavn || data.ViamapTingBog?.stamOplysninger?.indskannetAktDokumentId);
          const showDownloadButton =
            access(Feature.DownloadAktFraTinglysning) &&
            shouldRenderTingbogDownloadButton(hæftelse);
          const downloadTarget = showDownloadButton
            ? getTingbogDownloadTarget(hæftelse)
            : undefined;
          const haeftelsePdfActionAvailability = getTingbogPdfActionAvailability({
            downloadTarget,
            viewerAktId,
            viewerContextType: AnnotationContextType.Ejendom,
            viewerBfeNumber: propertyAnnotationId,
          }, access);
          haeftelsePdfAction = haeftelsePdfActionAvailability.hasAnyActions ? (
            <TingbogPdfActions
              downloadTarget={downloadTarget}
              downloadFileName={getTingbogDownloadFileName(
                `haeftelse_${recordNo || hæftelse.dokumentId || hæftelse.haeftelsesType}`
              )}
              viewerAktId={viewerAktId}
              viewerContextType={AnnotationContextType.Ejendom}
              viewerBfeNumber={propertyAnnotationId}
              viewerDocumentFilename={getTingbogDownloadFileName(
                `haeftelse_${recordNo || hæftelse.dokumentId || hæftelse.haeftelsesType}`
              )}
            />
          ) : null;
        }
        let tekster = hæftelse.tekster?.length ? <>{hæftelse.tekster?.map?.((a, idx) => <div style={{ whiteSpace: "normal" }} key={idx}>{a}</div>)}</> : undefined
        return (
          <Fragment key={hæftelse.dokumentId}>
            <ValueGroup >
              <TingbogEntryRow
                title={hæftelse.haeftelsesType + " - " + hæftelse.tinglysningsDato}
                action={haeftelsePdfAction}
              >
                <div>{hæftelse.kreditorDisplayText}</div>
                <div>{hæftelse.beloebVaerdi + " " + hæftelse.valutakode}</div>
              </TingbogEntryRow>
              <KeyValueLine valKey={hæftelse.senestPaategnetDato ? <span style={{ color: "var(--dangerColor)" }}>Påtegnet</span> : ""} keepEmpty={hæftelse.senestPaategnetDato ? true : false} value={tekster} />
            </ValueGroup>
          </Fragment>
        );
      }) || Localization.getText("No data")
    );
  }

  static TingbogServitutter(
    data: PropertyData,
    access: (a: FeatureType) => boolean,
  ): any {
    if (isTingbogUnavailable(data)) {
      return getTingbogUnavailableText();
    }

    const rows = data.ViamapTingBog?.servitutter?.toSorted((a, b) => {
      const IA = (d) => Number(d.prioritetNummer) || 10000;
      if (IA(a) < IA(b)) return -1;
      if (IA(a) > IA(b)) return 1;
      return 0;
    })?.map((servitut) => {
      return (
        <Fragment key={servitut.aktId || servitut.aktAliasId}>
          <ServitutRow servitut={servitut} hasAccess={access} stamOplysninger={data.ViamapTingBog?.stamOplysninger} viamapTingBog={data.ViamapTingBog} propertyData={data} />
        </Fragment>
      )
    });

    if (!rows || rows.length === 0) return Localization.getText("No data");

    return (
      <>
        {rows}
      </>
    );
  }

  static TingbogAdkomster(
    data: PropertyData,
    access: (a: FeatureType) => boolean,
  ): any {
    if (isTingbogUnavailable(data)) {
      return getTingbogUnavailableText();
    }

    return (
      <ValueGroup>
        <OpenInAdkomstView
          adkomster={data.ViamapTingBog?.adkomster as any}
          text={Localization.getText("View adkomst details and bilag")}
          allowDownload={access(Feature.DownloadAktFraTinglysning)}
          bfeNumber={getPropertyAnnotationIdentifier(data)}
          stamOplysninger={data.ViamapTingBog?.stamOplysninger}
        />
        <KeyValueLine
          keepEmpty
          valKey="Adkomsthaver"
          value={data.ViamapTingBog?.SenesteAdkomst?.adkomsthaver
            ?.map((a) => {
              if ("birthDate" in a && a.birthDate) {
                return `${a.displayText} - ${(a.birthDate as string).replace(/(\d+)-(\d+)-(\d+)/gm, `$3.$2.$1`)}`
              }
              return a.displayText
            }).join("\n ")
          }
        />
        <KeyValueLine
          keepEmpty
          valKey="Tinglysningsdato"
          value={data.ViamapTingBog?.SenesteAdkomst?.tinglysningsDato}
        />
        <KeyValueLine
          keepEmpty
          valKey="Kontantkøbesum"
          value={data.ViamapTingBog?.SenesteAdkomst?.kontantKoebesum}
        />
        <KeyValueLine
          keepEmpty
          valKey="I alt købesum"
          value={data.ViamapTingBog?.SenesteAdkomst?.iAltKoebesum}
        />
      </ValueGroup>
    );
  }

  static HandelsOplysninger(data: PropertyData) {
    if (!data.ViamapHandelsOplysning) {
      return <></>;
    }

    return data.ViamapHandelsOplysning?.handel?.features?.sort?.((a, b) => {
      // sort by date, descending
      return (
        new Date(b.properties.virkningFra).getTime() -
        new Date(a.properties.virkningFra).getTime()
      );
    }).map((feature, idx) => {
      let props = feature.properties;
      let tidspunkt = FormatUtils?.formatDate(new Date(props.virkningFra));

      let ejersk = data.ViamapHandelsOplysning?.ejerskifte?.features?.find?.(
        (feat) => { return (feat.properties.handelsoplysningerId === feature.properties.id_lokalId); }
      );
      let maade = ejersk && ejersk.properties.overdragelsesmaade;
      let forretningsh = props.forretningshaendelse !== "Ukendt - konverteret fra ESR" ? props.forretningshaendelse : null;
      let status = props.status !== "gældende" ? props.status : null;

      return (
        <ValueGroup key={props.id_lokalId}>
          <KeyValueLine valKey="Virkning fra" value={tidspunkt} />
          <KeyValueLine valKey="Forretningshændelse" value={forretningsh} />
          <KeyValueLine valKey="Overdragelsesmåde" value={maade} />
          <KeyValueLine valKey="Status" value={status} />
          <KeyValueLine valKey="Samlet købesum" value={props.samletKoebesum} />
          <KeyValueLine valKey="Valuta" value={props.valuta} />
          {props.skoedetekst ?
            <PaddedContainer>
              <GlassFoldUdBox variant="Small" title="Skødets tekst" foldetUd={idx == 0}>
                <KeyValueLine valKey="" value={props.skoedetekst} />
              </GlassFoldUdBox>
            </PaddedContainer>
            : null}
        </ValueGroup>
      )
    }) || [];
  }

  static Ejendommen(data: PropertyData, mini?: boolean) {
    if (!data.ViamapEjendomData) {
      return [];
    }


    let sfe = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
    let aktuelleMatrikler = (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((a) => a.status === "Gældende" && a.samletFastEjendomLokalId === sfe?.id_lokalId)
    let extraMatrikler = (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || []).length - aktuelleMatrikler.length;
    let SfeMatData = PropertyInformation.formatMatrikler(
      aktuelleMatrikler, false
    );
    let ejd = data.ViamapEjendomData;
    let darKommune: any | undefined = undefined;
    if (ejd.kommuneNr) {
      darKommune = { key: "Kommune", name: "Kommune", val: BBRKodeLister.Kommunekode(ejd.kommuneNr).replace(" Kommune", "") }
    }

    return [
      { key: "Adresse", name: "Adresse", val: ejd.ejdAdresse },
      {
        key: "Antal Matrikler",
        name: "Antal Matrikler",
        val: aktuelleMatrikler.length + (extraMatrikler > 0 ? ` (+${extraMatrikler})` : ""),
      },
      aktuelleMatrikler.length > 5 || { key: "Matrikler", name: "Matrikler", val: SfeMatData },
      darKommune ?? { key: "Kommune", name: "Kommune", val: ejd.kommuneNavn },
      { key: "Kategori", name: "Kategori", val: ejd.ejendomsKategori },
      { key: "Type", name: "Type", val: ejd.ejdtype },
    ].filter((a) => a.key).filter((a, idx) => !mini || idx < 2);
  }

  static EjerforholdData(data: PropertyData) {
    return (data.ViamapEjendomData?.ejere || []).map((ejer, idx) => {
      let cvrLink =
        "https://datacvr.virk.dk/enhed/virksomhed/" + ejer.CVRNummer;
      // ToDo: remove hack by changing the backend web service.
      let isRoad = Boolean(ejer.displayText == "ukendt ejertype, ingen adresse, intet postnummer intet postdistrikt");
      let renderCVRInfo = ejer.CVRNummer ? (
        <a
          href={cvrLink}
          className="no-print"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textAlign: "left", fontWeight: "600", color: "black" }}
        >
          (Link til cvr.dk)
        </a>
      ) : null;
      let renderReklamebeskyttelse = ejer.reklameBeskyttet
        ? `(${Localization.getText("Reklamebeskyttet")})`
        : "";
      return (
        <div
          key={ejer.displayText + idx}
          style={{ textAlign: "left", paddingLeft: "18px", fontWeight: "bold", fontSize: "15px" }}
          className="mit-geo-details-popup"
        >
          {isRoad ? Localization.getText("No data") :
            ejer.CVRNummer ? <RenderLinkToCompanyInfo cvrNr={ejer.CVRNummer}>{ejer.displayText}</RenderLinkToCompanyInfo> :
              <RenderLinkToPersonInfo {...(ejer as any)} dateOfBirth={ejer.foedselsdato && ejer.foedselsdato.length !== 0 ? new Date(ejer.foedselsdato) : "undefined"} > {ejer.displayText}</RenderLinkToPersonInfo>
          }&nbsp;{renderReklamebeskyttelse}
          {renderCVRInfo}
        </div>
      );
    });
  }

  static Ejerforhold(data: PropertyData) {

    return ("ViamapEjerskab" in data && data.ViamapEjerskab as any)?.map((ejer, idx) => {
      let cvrLink =
        "https://datacvr.virk.dk/enhed/virksomhed/" + ejer.CVRNummer;
      // ToDo: remove hack by changing the backend web service.
      let isRoad = Boolean(ejer.displayText == "ukendt ejertype, ingen adresse, intet postnummer intet postdistrikt");
      let renderCVRInfo = ejer.CVRNummer ? (
        <a
          href={cvrLink}
          className="no-print"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textAlign: "left", fontWeight: "600", color: "black" }}
        >
          (Link til cvr.dk)
        </a>
      ) : null;
      let renderReklamebeskyttelse = ejer.reklameBeskyttet
        ? `(${Localization.getText("Reklamebeskyttet")})`
        : "";
      return (
        <div
          key={ejer.displayText + idx}
          style={{ textAlign: "left", paddingLeft: "18px", fontWeight: "bold", fontSize: "15px" }}
          className="mit-geo-details-popup"
        >
          {isRoad ? Localization.getText("No data") :
            ejer.CVRNummer ? <RenderLinkToCompanyInfo cvrNr={ejer.CVRNummer}>{ejer.displayText}</RenderLinkToCompanyInfo> :
              <RenderLinkToPersonInfo {...(ejer as any)} dateOfBirth={ejer.foedselsdato && ejer.foedselsdato.length !== 0 ? new Date(ejer.foedselsdato) : "undefined"} > {ejer.displayText}</RenderLinkToPersonInfo>
          }&nbsp;{renderReklamebeskyttelse}
          {renderCVRInfo}
        </div>
      );
    });
  }

  static MatriklerTable(data: PropertyData, hasAccessToFeature: (Feature) => boolean, matrnr?: string, ejerlavskode?: string) {
    const selectedID = `${matrnr || ""}${ejerlavskode || ""}`;
    let sfe = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
    if (!sfe) {
      return <></>;
    }
    let matrikler = sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [];
    if (matrikler.length === 0) {
      return <></>;
    }
    const matSort = (a: Mat_Jordstykke, b: Mat_Jordstykke) => {
      let parseEjerlav =
        parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode, 10) -
        parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode, 10);
      if (parseEjerlav !== 0) {
        return parseEjerlav;
      }
      return PropertyInformation.matrikelSortFunc(
        a.matrikelnummer || "",
        b.matrikelnummer || ""
      );
    };
    const filterGaeldende = (elm: any) => {
      return elm?.status === "Gældende";
    };
    const filterIkkeFælleslod = (elm: any) => {
      return elm?.samletFastEjendomLokalId === sfe.id_lokalId;
    };
    let restMatData = matrikler
      ?.filter(filterGaeldende)
      .filter(filterIkkeFælleslod)
      .sort(matSort)

    return (
      <KeyValueTable>
        <tr>
          <th>Nr.</th>
          <th>Ejerlav</th>
          <th style={{ textAlign: "right" }}>Areal</th>
          <th style={{ textAlign: "right" }}>Heraf Vej</th>
        </tr>
        {restMatData?.map((a) => {
          const id = a.matrikelnummer + a.jordstykkeLiggerIEjerlav?.ejerlavskode;
          return (
            <tr key={id} className={a.faelleslod ? "faelleslod" : ""}
              style={{
                fontWeight: selectedID === id ? 900 : "inherit",
                color: selectedID ? (selectedID === id ? "var(--primaryColor)" : "#444") : "inherit",
              }}
            >
              <td>{hasAccessToFeature(Feature.LinkToMao) ? <a target="_blank" href={`https://mao.gst.dk/register.aspx?ejerlavskode=${a.jordstykkeLiggerIEjerlav?.ejerlavskode}&matrnr=${a.matrikelnummer}`}>{a.matrikelnummer}</a> : a.matrikelnummer}</td>
              <td>
                {a.jordstykkeLiggerIEjerlav?.ejerlavsnavn} ({a.jordstykkeLiggerIEjerlav?.ejerlavskode})
                {a.faelleslod ? " (fælleslod)" : ""}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(
                  Number(a.registreretAreal)
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(Number(a.vejareal))}
              </td>
            </tr>
          );
        })}
        <tr className="bold">
          <td></td>
          <td></td>
          <td style={{ textAlign: "right" }}>
            {new Intl.NumberFormat("da-dk").format(
              restMatData?.reduce((a, b) => {
                if (b.faelleslod) {
                  return a;
                }
                return Number(b.registreretAreal) + a;
              }, 0) || 0
            )}
          </td>
          <td style={{ textAlign: "right" }}>
            {new Intl.NumberFormat("da-dk").format(
              restMatData?.reduce((a, b) => {
                if (b.faelleslod) {
                  return a;
                }
                return (
                  (isNaN(Number(b.vejareal)) ? 0 : Number(b.vejareal)) + a
                );
              }, 0) || 0
            )}
          </td>
        </tr>
      </KeyValueTable>
    );
  }

  static MatriklerTableGQL(data: PropertyData["GQL_BFE"], hasAccessToFeature: (Feature) => boolean) {
    const sfeNode = data?.MAT_SamletFastEjendom?.nodes?.[0];
    if (!sfeNode) {
      return <></>;
    }

    const jordstykker = sfeNode.jordstykkeSamlesISamletFastEjendom?.nodes || [];
    if (jordstykker.length === 0) {
      return <></>;
    }

    const matSort = (a: any, b: any) => {
      const parseEjerlav =
        parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10) -
        parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
      if (parseEjerlav !== 0) {
        return parseEjerlav;
      }
      return PropertyInformation.matrikelSortFunc(
        a.matrikelnummer || "",
        b.matrikelnummer || ""
      );
    };

    const filterGaeldende = (elm: any) => {
      return elm?.status === "Gældende";
    };

    const filterIkkeFælleslod = (elm: any) => {
      return elm.samletFastEjendomLokalId === sfeNode.id_lokalId;
    };

    // Deduplicate by id_lokalId
    const seen = new Set<string>();
    const uniqueJordstykker = jordstykker.filter((j) => {
      const id = j.id_lokalId;
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });

    const restMatData = uniqueJordstykker
      .filter(filterGaeldende)
      .filter(filterIkkeFælleslod)
      .sort(matSort)
      .map((j) => ({
        matrikelnummer: j.matrikelnummer,
        ejerlavskode: j.jordstykkeLiggerIEjerlav?.ejerlavskode,
        ejerlavsnavn: j.jordstykkeLiggerIEjerlav?.ejerlavsnavn,
        registreretAreal: j.registreretAreal,
        vejareal: j.vejareal,
        faelleslod: j.faelleslod,
        samletFastEjendomLokalId: j.samletFastEjendomLokalId,
      }));

    return (
      <KeyValueTable>
        <tr>
          <th>Nr.</th>
          <th>Ejerlav</th>
          <th style={{ textAlign: "right" }}>Areal</th>
          <th style={{ textAlign: "right" }}>Heraf Vej</th>
        </tr>
        {restMatData.map((a) => {
          const id = a.matrikelnummer + a.ejerlavskode;
          return (
            <tr key={id} className={a.samletFastEjendomLokalId !== sfeNode.id_lokalId ? "faelleslod" : ""}>
              <td>
                {hasAccessToFeature(Feature.LinkToMao) ? (
                  <a target="_blank" href={`https://mao.gst.dk/register.aspx?ejerlavskode=${a.ejerlavskode}&matrnr=${a.matrikelnummer}`}>
                    {a.matrikelnummer}
                  </a>
                ) : (
                  a.matrikelnummer
                )}
              </td>
              <td>
                {a.ejerlavsnavn} ({a.ejerlavskode})
                {a.faelleslod ? " (fælleslod)" : ""}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(Number(a.registreretAreal) || 0)}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(Number(a.vejareal) || 0)}
              </td>
            </tr>
          );
        })}
        <tr className="bold">
          <td></td>
          <td></td>
          <td style={{ textAlign: "right" }}>
            {new Intl.NumberFormat("da-dk").format(
              restMatData.reduce((acc, b) => {
                if (b.samletFastEjendomLokalId !== sfeNode.id_lokalId) {
                  return acc;
                }
                return (b.registreretAreal || 0) + acc;
              }, 0)
            )}
          </td>
          <td style={{ textAlign: "right" }}>
            {new Intl.NumberFormat("da-dk").format(
              restMatData.reduce((acc, b) => {
                if (b.samletFastEjendomLokalId !== sfeNode.id_lokalId) {
                  return acc;
                }
                return (isNaN(Number(b.vejareal)) ? 0 : Number(b.vejareal)) + acc;
              }, 0)
            )}
          </td>
        </tr>
      </KeyValueTable>
    );
  }

  static FaelleslodderTable(data: PropertyData, hasAccessToFeature: (Feature) => boolean) {
    const sfe = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
    if (!sfe) {
      return null;
    }
    const matSort = (a: Mat_Jordstykke, b: Mat_Jordstykke) => {
      let parseEjerlav =
        parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode, 10) -
        parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode, 10);
      if (parseEjerlav !== 0) {
        return parseEjerlav;
      }
      return PropertyInformation.matrikelSortFunc(
        a.matrikelnummer || "",
        b.matrikelnummer || ""
      );
    };
    const filterGaeldende = (elm: { status: string }) => {
      return elm?.status === "Gældende";
    };
    const filterErFælleslod = (elm: { samletFastEjendomLokalId: string }) => {
      return elm?.samletFastEjendomLokalId !== sfe.BFEnummer;
    };

    let restMatData = sfe.jordstykkeSamlesISamletFastEjendom?.nodes
      ?.filter(filterGaeldende)
      .filter(filterErFælleslod)
      .sort(matSort)
    return restMatData && restMatData?.length > 0 ? (
      <KeyValueTable>
        <tr>
          <th>Nr.</th>
          <th>Ejerlav</th>
          <th style={{ textAlign: "right" }}>Areal</th>
          <th style={{ textAlign: "right" }}>Heraf Vej</th>
        </tr>
        {restMatData?.map((a) => {
          const id = a.matrikelnummer + a.jordstykkeLiggerIEjerlav?.ejerlavskode;
          return (
            <tr key={id}>
              <td>{hasAccessToFeature(Feature.LinkToMao) ? <a target="_blank" href={`https://mao.gst.dk/register.aspx?ejerlavskode=${a.jordstykkeLiggerIEjerlav?.ejerlavskode}&matrnr=${a.matrikelnummer}`}>{a.matrikelnummer}</a> : a.matrikelnummer}</td>
              <td>
                {a.jordstykkeLiggerIEjerlav?.ejerlavsnavn} ({a.jordstykkeLiggerIEjerlav?.ejerlavskode})
                {a.faelleslod ? " (fælleslod)" : ""}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(
                  Number(a.registreretAreal)
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {new Intl.NumberFormat("da-dk").format(Number(a.vejareal))}
              </td>
            </tr>
          );
        })}

      </KeyValueTable>
    ) : null;
  }

  static MarkerMap(data: PropertyData) {

    return (
      <PropertyMapContainer location="Marker">
        <EmbeddedVectorMapEnhanced
          orthoPhoto={true}
          options={{
            cooperativeGestures: true,
            locale: {
              'CooperativeGesturesHandler.WindowsHelpText': 'Brug ctrl + scroll til at zoome på kortet',
              'CooperativeGesturesHandler.MacHelpText': 'Brug ⌘ + scroll til at zoome på kortet',
              'CooperativeGesturesHandler.MobileHelpText': 'Brug to fingre til at flytte kortet',
              'NavigationControl.ZoomIn': 'Zoom ind',
              'NavigationControl.ZoomOut': 'Zoom ud',
              'Popup.Close': 'Luk popup',
            }
          }}
          sources={{
            "sfe": eMapSource((data.SFEGeom.features?.lenth > 1 ? union(data.SFEGeom) : data.SFEGeom) ?? []),
            "marker": eMapSource(data.Marker?.marker ?? []),
            "markerInde": eMapSource((data?.Marker?.marker ?? []).map((a) => ({ ...a, geometry: a.intersect }))),
          }}
          layers={{
            "marker": eMapLayerOutline("marker", "marker", "blue"),
            "marker2": eMapLayerFill("marker2", "marker", "blue", 0.1),
            "markerInde": eMapLayerFill("markerInde", "markerInde", "green"),
            "markerInde2": eMapLayerOutline("markerInde2", "markerInde", "#949600"),
            "sfe": eMapLayerOutline("sfe", "sfe", "orange"),
            "markerNr": eMapLayerTextSymbol("markerNr", "markerInde", "Marknr"),
          }}
          cTitle="Markoversigt"
          cLegend={[
            { title: "Ejendomsgrænse", ico: "mit-patt-stroke-orange-4" },
            { title: "Markareal på ejendommen", ico: "mit-patt-fill-#0a0a-4" },
            { title: "Markareal udenfor ejendommen", ico: "mit-patt-fill-#00aa-4" },
          ]}
          onMapReady={(map) =>
            map.getMapPop().fitBounds(bbox(featureCollection(data.Marker?.marker as any ?? [])) as [number, number, number, number], { padding: 20 })
          }
        />
      </PropertyMapContainer>
    )
  }



  static MarkerTable(data: PropertyData) {
    if (data.Marker?.marker) {
      let marker = data.Marker.marker
      let haFormat = MarkData.haFormat();
      let nrFormat = (nr: number) => haFormat(nr).format;

      return (
        <>
          <KeyValueLine valKey="Antal marker" value={marker.length} />
          <KeyValueLine valKey="Årstal" value={data.Marker.årstal} />
          <KeyValueTable>
            <tr>
              <th>Nr.</th>
              <th>Markblok</th>
              <th>Afgrøde</th>
              <th>På ejd.</th>
              <th>Areal</th>
              <th>Ansøgt</th>
            </tr>
            {marker.toSorted(MarkData.MarkDataSorter).map((mark, idx) => {
              let a = mark.properties;
              return (
                <tr key={idx}>
                  <td style={{ textWrap: "nowrap" }}>{BeautyString(a.Marknr)}</td>
                  <td style={{ textWrap: "nowrap" }}>{BeautyString(a.Markblok)}</td>
                  <td>{BeautyString(a.Afgroede)}</td>
                  <td style={{ textAlign: "right" }}>{nrFormat(10000 * a.IMK_areal * a.overlap / a.area)}</td>
                  <td style={{ textAlign: "right" }}>{nrFormat(10000 * a.IMK_areal)}</td>
                  <td style={{ textAlign: "right", color: a.GB ? "" : "#ef800b" }}>{nrFormat(10000 * a.GBanmeldt)}</td>
                </tr>
              );
            })}
            <tr className="bold">
              <td colSpan={3} style={{ textAlign: "right" }}>Total</td>
              <td style={{ textAlign: "right", textWrap: "nowrap" }}>{nrFormat(10000 * marker.map((a) => a.properties).reduce((sum, a) => sum + (a.IMK_areal * a.overlap / a.area), 0))}</td>
              <td style={{ textAlign: "right", textWrap: "nowrap" }}>{nrFormat(10000 * marker.map((a) => a.properties).reduce((sum, a) => sum + parseFloat(a.IMK_areal), 0))}</td>
              <td style={{ textAlign: "right", textWrap: "nowrap" }}>{nrFormat(10000 * marker.map((a) => a.properties).reduce((sum, a) => sum + parseFloat(a.GBanmeldt), 0))}</td>
            </tr>
          </KeyValueTable>
        </>
      );
    } else {
      return <></>;
    }
  }


  static MatriklerAndContent(data: PropertyData) {
    const matSort = (a, b) => {
      let parseEjerlav =
        parseInt(a.properties.ejerlavskode, 10) -
        parseInt(b.properties.ejerlavskode, 10);
      if (parseEjerlav !== 0) {
        return parseEjerlav;
      }
      return PropertyInformation.matrikelSortFunc(
        a.properties.matrikelnummer || "",
        b.properties.matrikelnummer || ""
      );
    };
    const filterGaeldende = (elm: any) => {
      return elm?.properties?.status === "Gældende";
    };
    let restMatData = data.BBRSFE?.[0]?.properties?.jordstykke
      ?.filter(filterGaeldende)
      .sort(matSort)
      .map((mat) => mat.properties);
    return (
      <KeyValueTable>
        <tr>
          <th>Nr.</th>
          <th>Ejerlav</th>
          <th>Areal</th>
          <th>Heraf Vej</th>
        </tr>
        <tbody>
          {restMatData?.map((a) => {
            const id = a.matrikelnummer + a.ejerlavskode;
            return (
              <tr key={id}>
                <td>{a.matrikelnummer}</td>
                <td>
                  {a.ejerlavsnavn} ({a.ejerlavskode})
                </td>
                <td>
                  {new Intl.NumberFormat("da-dk").format(
                    Number(a.registreretAreal)
                  )}
                </td>
                <td>
                  {new Intl.NumberFormat("da-dk").format(Number(a.vejareal))}
                </td>
              </tr>
            );
          })}
          <tr className="bold">
            <td></td>
            <td></td>
            <td>
              {new Intl.NumberFormat("da-dk").format(
                restMatData?.reduce((a, b) => {
                  return b.registreretAreal + a;
                }, 0) || 0
              )}
            </td>
            <td>
              {new Intl.NumberFormat("da-dk").format(
                restMatData?.reduce((a, b) => {
                  return (
                    (isNaN(Number(b.vejareal)) ? 0 : Number(b.vejareal)) + a
                  );
                }, 0) || 0
              )}
            </td>
          </tr>
        </tbody>
      </KeyValueTable>
    );
  }

  static PEnhederPaaEjendomFormatter(data: PropertyData) {
    let d = data?.PEnheder;



    return (<KeyValueTable key={"Penheder"}>
      <tr>
        <th style={{ textAlign: "left" }} >Virsomhed</th>
        <th style={{ textAlign: "left" }} >Branche</th>
        <th>Ansatte</th>
        <th>Årsværk</th>
      </tr>
      {
        d?.flatMap((a) => a.adresser.flatMap((a) => {
          if (a.pEnheder?.length === 0 || !a.pEnheder) {
            return (<Fragment key={a.adresseData.id} />)
          }
          return (
            <Fragment key={a.adresseData.id}>
              {/* <tr><td colSpan={5}>{a.adresseData.navn}</td></tr> */}
              {a.pEnheder.map((a) => {
                return (
                  <tr key={a.cvrNr}>
                    <td style={{ textAlign: "left" }} >{a.navn}<br /><RenderLinkToCompanyInfo cvrNr={a.cvrNr} >{a.cvrNr}</RenderLinkToCompanyInfo></td>
                    <td style={{ textAlign: "left" }} >{a.branchetekst}</td>
                    <td>{a.beskaeftigelseKvt?.antalAnsatte || a.beskaeftigelseAar?.antalAnsatte || ""}</td>
                    <td>{a.beskaeftigelseKvt?.antalAarsvaerk || a.beskaeftigelseAar?.antalAarsvaerk || ""}</td>
                  </tr>
                )
              })
              }
            </Fragment>
          )
        }))
      }
    </KeyValueTable>)
  }

  static BBRKeyValParser(key: string, value: any): any {
    if (value === null || value === undefined) {
      return ""
    }
    if (value instanceof Date) {
      return value.toLocaleString(Localization.getLocale())
    }
    if (value instanceof Array) {
      return ""
    }
    if (value instanceof Object) {
      return ""
    }
    let values = Object.values(ExportData.DoBBR(key, value))
    for (let i of values) {
      return i + PropInfoGroupFormatters.BBRSuffix(key)
    }
    return ""
  }

  static BBRSuffix(key: string): string {
    if (key.toLowerCase().includes("areal")) {
      return " m²"
    }
    return ""
  }

  static BBREvery(dataToParse) {
    let data = dataToParse
    if (!Array.isArray(dataToParse)) {
      data = [dataToParse]
    }
    return data.flat().map((atom) => {
      if (typeof atom !== "object") {
        return null
      }
      return (
        <ValueGroup key={(atom.id_lokalId + atom.status)} >
          {
            Object.keys(atom).filter(ExportData.BBRKeyFilter).map((a) => {
              return (
                <KeyValueLine
                  key={a}
                  valKey={a}
                  value={atom[a]}
                  KeyEndParser={(a) => { return ExportData.BBRDefaultKeyTranslator(a).toString() }}
                  CParser={(c, b) => {
                    let x = BBRTranslateByDataKey(a, b)
                    if (Array.isArray(x)) {
                      return ""
                    }
                    if (x instanceof Date) {
                      return x.toLocaleString(Localization.getLocale(), {
                        year: 'numeric', month: '2-digit', day: '2-digit'
                      })
                    }
                    return x.toString()
                  }}
                />
              )
            })
          }
        </ValueGroup>
      )


    })
  }

  static MarkedsDataNewsec(
    data: PropertyData & MarkedsData,
    foldetUd: boolean,
    access,
    select,
    setSelect
  ) {
    const minObservationer = 3;

    if (!access(Feature.ExplorerMarkedsData)) return [];

    const Mdata = data?.["NewSec" + select] as MarkedsDataIndv;
    if (!Mdata)
      return [
        <GlassFoldUdBox
          key={select + "Search"}
          group="Markedsdata"
          foldetUd={foldetUd}
          title={"Kriterier For Søgning"}
        >
          <GlassButton >{Localization.getText("Missing connection: Market data")}</GlassButton>
        </GlassFoldUdBox>,
      ];

    function formatNumberThousandsSep(value?: number) {
      return value
        ? value.toLocaleString("da-dk", { maximumFractionDigits: 0 })
        : "";
    }

    function SelectInput(
      label: string,
      prompt: string,
      options: string[],
      value: string,
      onChange: (value: string) => any
    ) {
      return (
        <Col>
          <FloatingLabel
            controlId={"floatingInput" + label}
            label={label}
            className="mb-1"
          >
            <Form.Select
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
              }}
            >
              {options.map((opt) => {
                return (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
      );
    }

    return [
      <GlassFoldUdBox
        key={select + "Salg"}
        group="Markedsdata"
        title={"Salg"}
        foldetUd={foldetUd}
      >
        {Mdata.PrisKvmData?.observationCount >= minObservationer ? (
          <KeyValueLine
            valKey="Gns. pris pr. m2"
            value={
              Mdata?.PrisKvmData.data && Mdata?.PrisKvmData.data.length > 0
                ? formatNumberThousandsSep(
                  Math.round(Mdata?.PrisKvmData.data[0].value)
                )
                : ""
            }
          />
        ) : (
          "For få observationer for området..."
        )}
        <GlassFoldUdBox title={"Detaljer"} foldetUd={false}>
          <KeyValueLine
            valKey="Observationer"
            value={Mdata?.PrisKvmData?.observationCount}
          />
          <KeyValueLine
            valKey="Tidligste år"
            value={new Date(Mdata?.PrisKvmData?.minDate).getFullYear()}
          />
          <KeyValueLine
            valKey="Seneste år"
            value={new Date(Mdata?.PrisKvmData?.maxDate).getFullYear()}
          />
          <KeyValueLine
            valKey="Nærmeste obs (m)"
            value={Mdata?.PrisKvmData?.minDistanceMeters.toFixed(0)}
          />
          <KeyValueLine
            valKey="Obs længst væk (m)"
            value={Mdata?.PrisKvmData?.maxDistanceMeters.toFixed(0)}
          />
        </GlassFoldUdBox>
      </GlassFoldUdBox>,
      <GlassFoldUdBox
        key={select + "Udlejning"}
        group="Markedsdata"
        title={"Udlejning"}
        foldetUd={foldetUd}
      >
        {Math.min(
          Mdata.NettoLejeData?.observationCount,
          Mdata.BruttoLejeData?.observationCount
        ) >= minObservationer ? (
          <>
            <KeyValueLine
              valKey="Gns. nettoleje"
              value={formatNumberThousandsSep(
                Math.round(Mdata?.NettoLejeData?.data[0]?.value || 0)
              )}
              infoText="Gennemsnitlig nettoleje i nye udlejninger, hvor lejemålets husleje er angivet som nettoleje"
            />
            <KeyValueLine
              valKey="Gns. bruttoleje"
              value={formatNumberThousandsSep(
                Math.round(Mdata?.BruttoLejeData?.data[0]?.value || 0)
              )}
              infoText="Gennemsnitlig bruttoleje i nye udlejninger, hvor lejemålets husleje er angivet som bruttoleje"
            />
          </>
        ) : (
          "For få observationer for området..."
        )}
        <GlassFoldUdBox title={"Detaljer"} foldetUd={false}>
          <KeyValueLine
            valKey="Observationer"
            value={Mdata?.NettoLejeData?.observationCount}
          />
          <KeyValueLine
            valKey="Tidligste år"
            value={new Date(Mdata?.NettoLejeData?.minDate).getFullYear()}
          />
          <KeyValueLine
            valKey="Seneste år"
            value={new Date(Mdata?.NettoLejeData?.maxDate).getFullYear()}
          />
          <KeyValueLine
            valKey="Nærmeste obs (m)"
            value={Mdata?.NettoLejeData?.minDistanceMeters.toFixed(0)}
          />
          <KeyValueLine
            valKey="Obs længst væk (m)"
            value={Mdata?.NettoLejeData?.maxDistanceMeters.toFixed(0)}
          />
        </GlassFoldUdBox>
      </GlassFoldUdBox>,
      <GlassFoldUdBox
        key={select + "Search"}
        group="Markedsdata"
        foldetUd={foldetUd}
        title={"Kriterier For Søgning"}
      >
        <KeyValueLine
          valKey="Target antal"
          value={SettingsManager.getSystemSetting("marketDataTargetCount")}
        />
        <KeyValueLine
          valKey="Max afstand (m)"
          value={SettingsManager.getSystemSetting("marketDataMaxDistance")}
        />
        <KeyValueLine
          valKey="Fra år"
          value={SettingsManager.getSystemSetting("marketDataStartYear")}
        />
        <KeyValueLine
          valKey="Til år"
          value={SettingsManager.getSystemSetting("marketDataEndYear")}
        />
        {SelectInput(
          "ejendomstype",
          "ejendomstype",
          ["Kontor", "Detail", "Industri", "Andet"],
          select,
          (val) => {
            setSelect(val);
          }
        )}
      </GlassFoldUdBox>,
    ];
  }

  static EnergyInfo(data: PropertyData): any {
    if (!data.EnergyLabel || !data.EnergyLabel.EnergyLabels || data.EnergyLabel.EnergyLabels.length === 0) {
      return (
        <div style={{
          padding: "12px 16px",
          textAlign: "center",
          color: "#6b7280",
          fontSize: "14px",
          fontStyle: "italic",
          backgroundColor: "#f9fafb",
          borderRadius: "6px",
          border: "1px solid #e5e7eb"
        }}>
          {Localization.getText("No energy label data available")}
        </div>
      );
    }

    const filteredLabels = filterLatestEnergyLabels(data.EnergyLabel.EnergyLabels);

    // Sort energy labels by building number
    const energySort = (a, b) => {
      const buildingNumberA = parseInt(a.Buildings[0]?.BuildingNumber?.replace(/^0+/, '') || '0', 10);
      const buildingNumberB = parseInt(b.Buildings[0]?.BuildingNumber?.replace(/^0+/, '') || '0', 10);
      return buildingNumberA - buildingNumberB;
    };

    const sortedLabels = filteredLabels.sort(energySort);

    return (
      <KeyValueTable>
        <tr>
          <th>{Localization.getText("Building")}</th>
          <th>{Localization.getText("Energy Classification")}</th>
          <th>{Localization.getText("Status")}</th>
          <th>{Localization.getText("Valid From")}</th>
          <th>{Localization.getText("Valid To")}</th>
          <th>Links</th>
        </tr>
        {sortedLabels.map((label) => {
          const buildingNumber = label.Buildings[0]?.BuildingNumber || 'N/A';
          const isExpired = label.LabelStatus === 'EXPIRED';
          const statusColor = isExpired ? '#d32f2f' : '#2e7d32';
          const statusBgColor = isExpired ? '#ffebee' : '#e8f5e8';

          return (
            <tr key={label.EnergyLabelSerialIdentifier}>
              <td>
                {label.StreetName} {label.HouseNumber} - {Localization.getText("Building")} {buildingNumber}
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EnergyLabelIcon classification={label.EnergyLabelClassification} size="small" />
                </div>
              </td>
              <td>
                <span style={{
                  backgroundColor: statusBgColor,
                  color: statusColor,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '12px'
                }}>
                  {Localization.getText(label.LabelStatus)}
                </span>
              </td>
              <td>{formatDate(label.ValidFrom)}</td>
              <td>{formatDate(label.ValidTo)}</td>
              <td>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <EnergyLabelPdfButton identifier={label.EnergyLabelSerialIdentifier} />
                  {label.DEMOLink && (
                    <ViewButtonIcon
                      onClick={() => window.open(label.DEMOLink, '_blank')}
                      title={Localization.getText("Gå til EnergiStyrelsens hjemmeside")}
                      style={{
                        backgroundColor: 'transparent',
                        transition: 'background 0.2s',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onMouseOver={e => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.01)')}
                      onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <MIBIcon icon="link" color="green" />
                    </ViewButtonIcon>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </KeyValueTable>
    );
  }

  static DocumentsComponent(data: PropertyData): any {
    return <DocumentsComponent data={data} />;
  }
}

type MarkedsData =
  | {
    Kontor: MarkedsDataIndv;
    Detail: MarkedsDataIndv;
    Industri: MarkedsDataIndv;
    Andet: MarkedsDataIndv;
  }
  | undefined;

type MarkedsDataIndv = Record<
  "BruttoLejeData" | "NettoLejeData" | "VolumenData" | "PrisKvmData",
  {
    observationCount: number;
    data: {
      timeStamp: number;
      value: number;
    }[];
    minDate: string;
    maxDate: string;
    minDistanceMeters: number;
    maxDistanceMeters: number;
  }
>;

function OpenInServitutView(props: { text: string, uuid: string, servitut: any, stamOplysninger?: any, viamapTingBog?: any, propertyData?: any }) {
  const { dispatch: windowDispatch } = useWindowContext();

  function onClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    windowDispatch(openWindow(WindowId.ServitutDetails, buildServitutDetailsParams(props.servitut, {
      stamOplysninger: props.stamOplysninger,
      viamapTingBog: props.viamapTingBog,
      propertyData: props.propertyData
    })));
  }

  return <a onClick={onClick} href="" >{props.text}</a>;

}

function TingbogEntryRow(props: {
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="Line-MitProp tingbog-entry-row">
      <div className="tingbog-entry-content">
        <div className="tingbog-entry-header">
          <div className="tingbog-entry-title">{props.title}</div>
          {props.action ? (
            <div className="tingbog-entry-action">{props.action}</div>
          ) : null}
        </div>
        {props.children ? (
          <div className="tingbog-entry-body">{props.children}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Adkomst / hæftelse PDF dropdown (download, open in editor) — off until server-side
 * tinglysning pipeline supports these document types reliably. Set to `true` to restore UI.
 */
const ENABLE_TINGBOG_ADKOMST_HAEFTELSE_PDF_UI = true;

/** Digital skøde/hæftelse PDF via gateway is only offered for tinglysning on or after this instant. */
const TINGLYSNING_PDF_MIN_INSTANT_MS = Date.UTC(2009, 0, 1, 0, 0, 0, 0);

function parseTinglysningsDatoToUtcMs(raw: string | undefined): number | null {
  if (!raw?.trim()) {
    return null;
  }
  const s = raw.trim();
  const fromIso = Date.parse(s);
  if (!Number.isNaN(fromIso)) {
    return fromIso;
  }
  const dm = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(s);
  if (dm) {
    const day = Number(dm[1]);
    const month = Number(dm[2]) - 1;
    const year = Number(dm[3]);
    const t = Date.UTC(year, month, day);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Skøder/hæftelser before 2009 are not offered for PDF fetch (pre-digital / unreliable). */
export function isTinglysningPdfEligibleByDate(tinglysningsDato: string | undefined): boolean {
  const ms = parseTinglysningsDatoToUtcMs(tinglysningsDato);
  if (ms === null) {
    return false;
  }
  return ms >= TINGLYSNING_PDF_MIN_INSTANT_MS;
}

type TingbogAIAnalysisContext = {
  servitut: any;
  stamOplysninger?: any;
  viamapTingBog?: any;
  propertyData?: any;
};

type TingbogPdfActionsProps = {
  downloadTarget?: TingbogDownloadTarget;
  downloadFileName?: string;
  viewerAktId?: string;
  viewerContextType?: AnnotationContextType;
  viewerDocumentId?: string;
  viewerBfeNumber?: string | number;
  viewerDatoloebenr?: string;
  viewerDocumentFilename?: string;
  aiAnalysisContext?: TingbogAIAnalysisContext;
  beforeButtonContent?: React.ReactNode;
};

function getTingbogPdfActionAvailability(
  props: Pick<
    TingbogPdfActionsProps,
    "downloadTarget" |
    "viewerAktId" |
    "viewerContextType" |
    "viewerDocumentId" |
    "viewerBfeNumber" |
    "aiAnalysisContext"
  >,
  hasAccessToFeature: (feature: FeatureType) => boolean,
) {
  const isGatewayDokumentPdf =
    props.downloadTarget?.kind === "gatewayDokumentPdf";
  const hasViewerContext = props.viewerContextType === AnnotationContextType.Servitut
    ? Boolean(props.viewerDocumentId)
    : props.viewerContextType === AnnotationContextType.Ejendom
      ? Boolean(props.viewerBfeNumber || isGatewayDokumentPdf)
      : false;
  const canFetchNewTingbog = hasAccessToFeature(Feature.FetchNewTingbogAllow);
  const canDownload =
    Boolean(props.downloadTarget) &&
    (!isGatewayDokumentPdf || canFetchNewTingbog);
  const canOpenViaGateway = Boolean(isGatewayDokumentPdf && canFetchNewTingbog);
  const canOpenInEditor =
    hasViewerContext &&
    hasAccessToFeature(Feature.ServitutEditor) &&
    (Boolean(props.viewerAktId) || canOpenViaGateway);
  const canOpenWithAI =
    Boolean(props.viewerAktId) &&
    hasViewerContext &&
    props.viewerContextType === AnnotationContextType.Servitut &&
    Boolean(props.aiAnalysisContext) &&
    hasAccessToFeature(Feature.ServitutAIAnalyse);

  return {
    canDownload,
    canOpenInEditor,
    canOpenWithAI,
    hasAnyActions: canDownload || canOpenInEditor || canOpenWithAI,
  };
}

function getPropertyAnnotationIdentifier(propertyData?: Partial<PropertyData>): string | undefined {
  const value =
    propertyData?.BBRRel?.bfeNummer
    ?? propertyData?.BBRSFE?.[0]?.properties?.BFEnummer
    ?? propertyData?.ViamapEjendomData?.sag?.BFEnr
    ?? propertyData?.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer;

  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  return String(value);
}

/** Optional matrikel hints (e.g. from map click) when PropertyData has no GQL_BFE yet. */
export type TingbogAttestCadastralHints = {
  ejerlavskode?: string;
  matrikelnummer?: string;
  ejerlavsnavn?: string;
};

function firstBBRSFEEntry(data: PropertyData): any {
  const raw = data.BBRSFE as unknown;
  if (!raw) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    return raw[0];
  }
  return raw;
}

function resolveEjerlavsnavnFromPropertyData(
  data: PropertyData,
  ejerlavskode: string,
): string {
  const ejerlavskodeStr = String(ejerlavskode).replace(/\s+/g, "").trim();
  if (!ejerlavskodeStr) {
    return "";
  }
  const kodeNum = Number(ejerlavskodeStr);
  const ejerlavEntry = data.Ejerlav?.find(
    (e) => Number(e.kode) === kodeNum || String(e.kode) === ejerlavskodeStr,
  );
  const navn = typeof ejerlavEntry?.navn === "string" ? ejerlavEntry.navn.trim() : "";
  return navn;
}

function ejerlavskodeKey(code: string): string {
  return String(code).replace(/\s+/g, "").trim().padStart(7, "0");
}

function pickEjendomSummariskCadastral(
  data: PropertyData,
  hints?: TingbogAttestCadastralHints,
): { ejerlavskode: string; matrikelnummer: string; ejerlavsnavn: string } | null {
  const hE = hints?.ejerlavskode?.toString().trim();
  const hM = hints?.matrikelnummer?.toString().trim();
  const hN = hints?.ejerlavsnavn?.toString().trim() || "";
  if (hE && hM) {
    const ejerlavskodeStr = String(hE).replace(/\s+/g, "").trim();
    const navn =
      hN || resolveEjerlavsnavnFromPropertyData(data, ejerlavskodeStr);
    return { ejerlavskode: ejerlavskodeStr, matrikelnummer: hM, ejerlavsnavn: navn };
  }

  const mat2 = data.Mat2;
  const m2e = mat2?.ejerlavskode;
  const m2m = mat2?.matrikelnummer?.toString().trim();
  if (m2e !== undefined && m2e !== null && String(m2e).trim() !== "" && m2m) {
    const ejerlavskodeStr = String(m2e).replace(/\s+/g, "").trim();
    const kodeNum = Number(ejerlavskodeStr);
    const ejerlavEntry = data.Ejerlav?.find(
      (e) => Number(e.kode) === kodeNum || String(e.kode) === ejerlavskodeStr,
    );
    return {
      ejerlavskode: ejerlavskodeStr,
      matrikelnummer: m2m,
      ejerlavsnavn:
        (typeof ejerlavEntry?.navn === "string" ? ejerlavEntry.navn : "") || "",
    };
  }

  const sfe = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
  if (sfe) {
    const pool = filterCurrentJordstykkerForEjendomSummarisk(
      sfe.jordstykkeSamlesISamletFastEjendom?.nodes || [],
      sfe.id_lokalId,
    );
    type GqlJord = {
      matrikelnummer?: string;
      jordstykkeLiggerIEjerlav?: {
        ejerlavskode?: string | number;
        ejerlavsnavn?: string;
      };
    };
    const matHint =
      data.Mat2?.matrikelnummer?.toString().trim().toLowerCase() || "";
    let j: GqlJord | undefined;
    if (matHint) {
      j = pool.find((node) => {
        const m = (node as GqlJord).matrikelnummer?.toString().trim().toLowerCase();
        return m === matHint;
      }) as GqlJord | undefined;
    }
    if (!j) {
      j = pool[0] as GqlJord | undefined;
    }
    if (j) {
      const el = j.jordstykkeLiggerIEjerlav;
      const elc = el?.ejerlavskode?.toString().replace(/\s+/g, "").trim();
      const mat = j.matrikelnummer?.toString().trim();
      if (elc && mat) {
        return {
          ejerlavskode: elc,
          matrikelnummer: mat,
          ejerlavsnavn: el?.ejerlavsnavn?.toString().trim() || "",
        };
      }
    }
  }

  const bfeEntry = firstBBRSFEEntry(data);
  const jord = bfeEntry?.properties?.jordstykke;
  if (Array.isArray(jord) && jord.length > 0) {
    const js0 = jord[0];
    const p = js0?.properties ?? js0;
    const elc = p?.ejerlavskode?.toString().replace(/\s+/g, "").trim();
    const mat = p?.matrikelnummer?.toString().trim();
    if (elc && mat) {
      return {
        ejerlavskode: elc,
        matrikelnummer: mat,
        ejerlavsnavn: p?.ejerlavsnavn?.toString().trim() || "",
      };
    }
  }

  return null;
}

function buildEjendomSummariskParamsFromProperty(
  data: PropertyData,
  hints?: TingbogAttestCadastralHints,
): EjendomSummariskPdfRequest | null {
  const picked = pickEjendomSummariskCadastral(data, hints);
  if (!picked) {
    return null;
  }
  return {
    format: "pdf",
    cadastralDistrictIdentifier: ejerlavskodeKey(picked.ejerlavskode),
    matrikelnummer: picked.matrikelnummer,
  };
}

function TingbogsattestDownloadRow(props: {
  data: PropertyData;
  access: (feature: FeatureType) => boolean;
  cadastralHints?: TingbogAttestCadastralHints;
}) {

  const sfe = props.data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
  let matToParams = toEjendomSummariskMatrikelListe(
    sfe?.jordstykkeSamlesISamletFastEjendom?.nodes,
    sfe?.id_lokalId,
  );
  if (!matToParams.length) {
    const picked = pickEjendomSummariskCadastral(props.data, props.cadastralHints);
    if (picked) {
      matToParams = [{ elav: picked.ejerlavskode, matnr: picked.matrikelnummer }];
    }
  }

  const [busy, setBusy] = useState(false);
  if (!props.access(Feature.DownloadAktFraTinglysning)) {
    return null;
  }
  if (!props.access(Feature.FetchNewTingbogAllow)) {
    return null;
  }
  const params = buildEjendomSummariskParamsFromProperty(
    props.data,
    props.cadastralHints,
  );
  const allTingbogerReady = "ViamapTingBogAll" in props.data;
  const hasMultipleTingboger = listPropertyTingbogs(
    props.data.ViamapTingBogAll,
    props.data.ViamapTingBog,
  ).length > 1;
  const canDownload = matToParams.length > 0 && Boolean(params) && allTingbogerReady && !hasMultipleTingboger;
  const unavailableReason = hasMultipleTingboger
    ? Localization.getText("Tingbog attest unavailable for multiple registers")
    : !allTingbogerReady
      ? Localization.getText("Fetching other land registers…")
      : Localization.getText("Tingbog attest needs cadastral data");

  async function onDownload() {
    if (!matToParams.length || busy || hasMultipleTingboger) {
      return;
    }
    setBusy(true);
    try {
      await downloadEjendomSummariskPdf(
        matToParams,
        getTingbogAttestDownloadFileName(
          getPropertyAnnotationIdentifier(props.data)?.trim(),
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <KeyValueLine
      valKey={Localization.getText("Tingbog attest")}
      value={
        <div style={{ display: "flex", alignItems: "center", gap: "0.5em", justifyContent: "flex-end", width: "100%" }}>
          {canDownload ? (
            <GlassButton
              className="tingbog-download-button"
              disabled={busy}
              onClick={() => {
                void onDownload();
              }}
            >
              {busy ? (
                <Spinner animation="border" size="sm" className="tingbog-download-spinner" />
              ) : (
                <MIBIcon icon="download" />
              )}
              <span style={{ marginLeft: "0.35em" }}>
                {Localization.getText("Download tingbog attest")}
              </span>
            </GlassButton>
          ) : (
            <TooltipWrapper
              title={unavailableReason}
              className="ViewButtonToolTip"
            >
              <span className="text-muted">{Localization.getText("Tingbog attest unavailable")}</span>
            </TooltipWrapper>
          )}
        </div>
      }
    />
  );
}

function TingbogPdfActions(props: TingbogPdfActionsProps) {
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { state: sessionState } = useContext(SessionContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const busyRef = useRef(false);
  const progressTimersRef = useRef<NodeJS.Timeout[]>([]);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState<string>("");
  const isWorking = loadingAction !== null;

  const clearProgressTimers = () => {
    progressTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    progressTimersRef.current = [];
  };

  useEffect(() => {
    return () => {
      clearProgressTimers();
    };
  }, []);

  const actionAvailability = getTingbogPdfActionAvailability(props, hasAccessToFeature);

  async function openPdfViewer(runAIAnalysis = false) {
    const gatewayTarget =
      props.downloadTarget?.kind === "gatewayDokumentPdf"
        ? props.downloadTarget
        : undefined;
    if (!props.viewerAktId && !gatewayTarget) {
      appMessageDispatch(actionSetErrorMessage(
        Localization.getText("No PDF available for this document")
      ));
      return;
    }

    const isDigitalTingbogPdf = Boolean(gatewayTarget);

    try {
      clearProgressTimers();
      setLoadingText(Localization.getText(
        runAIAnalysis
          ? "Fetching Akt samling..."
          : isDigitalTingbogPdf
            ? "Opening PDF..."
            : "Opening scanned akt PDF..."
      ));
      if (!runAIAnalysis) {
        progressTimersRef.current.push(setTimeout(() => {
          setLoadingText(Localization.getText("The action takes longer than expected"));
        }, 10000));
      }

      let analysisResult: any;
      let documentFilename =
        props.viewerDocumentFilename ||
        props.viewerAktId ||
        gatewayTarget?.dokumentIdentifikator;

      if (runAIAnalysis) {
        if (!props.aiAnalysisContext) {
          appMessageDispatch(actionSetErrorMessage(
            Localization.getText("No PDF available for this document")
          ));
          return;
        }

        if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
          appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
          return;
        }

        if (!gatewayTarget && !props.viewerAktId) {
          appMessageDispatch(actionSetErrorMessage(
            Localization.getText("No PDF available for this document")
          ));
          return;
        }

        const analyzer = createANNLLMAnalyzer({
          userRef: sessionState.userRef,
          userName: sessionState.userName,
          organizationId: sessionState.customerRef,
        });
        const servitut = props.aiAnalysisContext.servitut || {};

        setLoadingText(Localization.getText("Analyzing with AI..."));
        progressTimersRef.current.push(setTimeout(() => {
          setLoadingText(Localization.getText("AI analysis might take some time..."));
        }, 10000));
        progressTimersRef.current.push(setTimeout(() => {
          let stillWorkingCount = 1;
          setLoadingText(Localization.getText("Still working..."));
          const updateStillWorking = () => {
            stillWorkingCount++;
            const dots = ".".repeat(Math.min(stillWorkingCount, 100));
            setLoadingText(`${Localization.getText("Still working")}${dots}`);
            if (stillWorkingCount < 100) {
              const timeoutId = setTimeout(updateStillWorking, 2000);
              progressTimersRef.current.push(timeoutId);
            }
          };
          const timeoutId = setTimeout(updateStillWorking, 2000);
          progressTimersRef.current.push(timeoutId);
        }, 20000));

        try {
          const metadata = await analyzer.buildServitutUrlAnalysisMetadata({
            servitut,
            stamOplysninger: props.aiAnalysisContext.stamOplysninger,
            viamapTingBog: props.aiAnalysisContext.viamapTingBog,
            propertyData: props.aiAnalysisContext.propertyData,
            fetchAktId: props.viewerAktId!,
          });
          documentFilename = metadata.document;
          analysisResult = await analyzer.analyzePdfUrlBatch([metadata]);
        } catch (llmError) {
          console.error("AI analysis failed for Tingbog document", llmError);
          appMessageDispatch(actionSetInfoMessage(
            Localization.getText("AI analysis failed, showing PDF without highlights")
          ));
        }
      }

      const pdfBlob = gatewayTarget
        ? await fetchTingbogDokumentPdfBlob(
          gatewayTarget.dokumentIdentifikator,
          gatewayTarget.revisionDokumentIdentifikator,
        )
        : await fetchTingbogPdfBlob(props.viewerAktId!);

      const pdfUrl = URL.createObjectURL(pdfBlob);
      windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
        pdfUrl,
        pdfBlob,
        analysisResult,
        aktId: props.viewerAktId ?? gatewayTarget?.dokumentIdentifikator,
        documentId: props.viewerContextType === AnnotationContextType.Servitut
          ? props.viewerDocumentId
          : undefined,
        bfeNumber: props.viewerContextType === AnnotationContextType.Ejendom
          ? props.viewerBfeNumber
          : undefined,
        contextType: props.viewerContextType,
        organizationId: sessionState?.customerRef,
        userId: sessionState?.userRef,
        userName: sessionState?.userName,
        datoloebenr: props.viewerDatoloebenr,
        documentFilename,
        treatEmptyPagesKeptAsAll: isDigitalTingbogPdf,
      }));
    } catch (err: any) {
      appMessageDispatch(actionSetErrorMessage(
        err?.message || Localization.getText(
          runAIAnalysis
            ? "Failed to analyze PDF"
            : isDigitalTingbogPdf
              ? "Failed to open PDF"
              : "Failed to open scanned akt PDF"
        )
      ));
    }
  }

  async function runAction(actionKey: string, action: () => Promise<void>) {
    if (busyRef.current) {
      return;
    }

    busyRef.current = true;
    setLoadingAction(actionKey);
    try {
      await action();
    } finally {
      busyRef.current = false;
      clearProgressTimers();
      setLoadingText("");
      setLoadingAction(null);
    }
  }

  const actions = [
    actionAvailability.canDownload && hasAccessToFeature(Feature.Debugging) ? {
      key: "logXml",
      label: "Log Xml",
      run: async () => {
        clearProgressTimers();
        setLoadingText(Localization.getText("Downloading..."));
        progressTimersRef.current.push(setTimeout(() => {
          setLoadingText(Localization.getText("The action takes longer than expected"));
        }, 10000));
        if (props.downloadTarget?.kind === "gatewayDokumentPdf") {
          console.log(await fetchTingbogDocumentXML(props.downloadTarget.dokumentIdentifikator));
        } else {
          message.info("XML is not available for this document");
        }
      }
    } : null,
    actionAvailability.canDownload ? {
      key: "download",
      label: Localization.getText("Download PDF"),
      run: async () => {
        clearProgressTimers();
        setLoadingText(Localization.getText("Downloading..."));
        progressTimersRef.current.push(setTimeout(() => {
          setLoadingText(Localization.getText("The action takes longer than expected"));
        }, 10000));
        await downloadTingbogDocument(props.downloadTarget, props.downloadFileName);
      }
    } : null,
    actionAvailability.canOpenInEditor ? {
      key: "editor",
      label: Localization.getText("Open PDF in editor"),
      run: async () => {
        await openPdfViewer(false);
      }
    } : null,
    actionAvailability.canOpenWithAI ? {
      key: "ai",
      label: Localization.getText("Open PDF with AI analysis"),
      run: async () => {
        await openPdfViewer(true);
      }
    } : null,
  ].filter(Boolean) as { key: string; label: string; run: () => Promise<void> }[];

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className="tingbog-download-actions">
      {isWorking && loadingText ? (
        <span className="tingbog-download-status-text">{loadingText}</span>
      ) : null}
      {props.beforeButtonContent ? props.beforeButtonContent : null}
      <GlassButtonDropdown
        className={`tingbog-download-button tingbog-download-button-dropdown${isWorking ? " tingbog-download-button-dropdown--loading" : ""}`}
        title={isWorking ? (loadingText || Localization.getText("Loading...")) : Localization.getText("PDF actions")}
        placement="bottom-end"
        onClick={(e) => {
          if (isWorking) {
            e.preventDefault();
          }
        }}
        options={actions.map((action) => ({
          text: action.label,
          value: action.key,
        }))}
        onSelect={async (value) => {
          const action = actions.find((entry) => entry.key === value);
          if (!action) return;
          await runAction(action.key, action.run);
        }}
      >
        {isWorking ? (
          <Spinner animation="border" size="sm" className="tingbog-download-spinner" />
        ) : (
          <MIBIcon icon="download" />
        )}
      </GlassButtonDropdown>
    </div>
  );
}

function OpenInAdkomstView(props: {
  text: string,
  adkomster: {
    tinglysningsDato: string,
    dokumentId?: string,
    aktId?: string,
    aktAliasId?: string,
    dokumentFilnavn?: string,
  }[],
  allowDownload?: boolean,
  bfeNumber?: string | number,
  stamOplysninger?: { indskannetAktDokumentId?: string },
}) {
  const { dispatch: windowDispatch } = useWindowContext();
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  if (!props.adkomster?.length) {
    return <></>
  }

  const senesteAdkomst = [...props.adkomster].sort((a, b) => {
    return new Date(b.tinglysningsDato).getTime() - new Date(a.tinglysningsDato).getTime()
  })[0];
  if (!senesteAdkomst) {
    return <></>;
  }

  let adkomstPdfActionsValue: React.ReactNode = "";
  if (
    ENABLE_TINGBOG_ADKOMST_HAEFTELSE_PDF_UI &&
    isTinglysningPdfEligibleByDate(senesteAdkomst.tinglysningsDato)
  ) {
    const isDigitalizedAdkomst = isTingbogElectronicDocument(senesteAdkomst);
    const adkomstFilnavn = senesteAdkomst.dokumentFilnavn?.trim();
    const viewerAktId = isDigitalizedAdkomst
      ? undefined
      : (adkomstFilnavn || props.stamOplysninger?.indskannetAktDokumentId);
    const showDownloadButton =
      props.allowDownload && shouldRenderTingbogDownloadButton(senesteAdkomst);
    const downloadTarget = showDownloadButton
      ? getTingbogDownloadTarget(senesteAdkomst)
      : undefined;
    const adkomstPdfActionAvailability = getTingbogPdfActionAvailability({
      downloadTarget,
      viewerAktId,
      viewerContextType: AnnotationContextType.Ejendom,
      viewerBfeNumber: props.bfeNumber,
    }, hasAccessToFeature);
    const downloadFileName = getTingbogDownloadFileName(
      `adkomst_${TingBogRecordNumber(senesteAdkomst?.aktId || senesteAdkomst?.aktAliasId || "") || senesteAdkomst?.dokumentId || props.text}`
    );
    adkomstPdfActionsValue = adkomstPdfActionAvailability.hasAnyActions ? (
      <TingbogPdfActions
        downloadTarget={downloadTarget}
        downloadFileName={downloadFileName}
        viewerAktId={viewerAktId}
        viewerContextType={AnnotationContextType.Ejendom}
        viewerBfeNumber={props.bfeNumber}
        viewerDocumentFilename={downloadFileName}
      />
    ) : "";
  }

  function onClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    windowDispatch(openWindow(WindowId.AdkomstDetails, {
      adkomst: senesteAdkomst,
      bfeNumber: props.bfeNumber,
      stamOplysninger: props.stamOplysninger,
      allowDownload: props.allowDownload,
    }));
  }

  const canShowAdkomstPdfUi =
    props.allowDownload &&
    ENABLE_TINGBOG_ADKOMST_HAEFTELSE_PDF_UI &&
    isTinglysningPdfEligibleByDate(senesteAdkomst.tinglysningsDato);
  const canShowAdkomstRow =
    hasAccessToFeature(Feature.AdkomstDetaljer) || canShowAdkomstPdfUi;
  if (!canShowAdkomstRow) {
    return <></>;
  }
  const documentLabel = (
    <span className="tingbog-adkomst-document-inline">
      {Localization.getText("Document")}{" "}
      {hasAccessToFeature(Feature.AdkomstDetaljer)
        ? <a onClick={onClick} href="" >{props.text}</a>
        : <span>{props.text}</span>}
    </span>
  );
  return <KeyValueLine
    keepEmpty
    valKey={documentLabel}
    value={adkomstPdfActionsValue}
  />;
}


export function ServitutTingbogPdfActions(props: {
  servitut: any;
  stamOplysninger?: any;
  viamapTingBog?: any;
  propertyData?: any;
  hasAccess: (feature: FeatureType) => boolean;
}) {
  const { servitut, stamOplysninger, viamapTingBog, propertyData, hasAccess } = props;
  const recordNo = TingBogRecordNumber(servitut.aktId || servitut.aktAliasId);
  const downloadFileName = getServitutMainDocumentFileName(servitut);
  const isDigitalizedServitut = isTingbogElectronicDocument(servitut);
  const searchTargets = getServitutAktSearchTargets(servitut, stamOplysninger);
  const viewerAktId = isDigitalizedServitut
    ? undefined
    : getServitutPdfFetchAktId(servitut, stamOplysninger) ?? undefined;
  const isUsingKorrektAkt = !isDigitalizedServitut
    && Boolean(searchTargets.korrektAktId)
    && searchTargets.korrektAktId !== searchTargets.servitutAktId;
  const isUsingTextAkt = !isDigitalizedServitut
    && !searchTargets.korrektAktId
    && !searchTargets.servitutAktId
    && Boolean(searchTargets.textAktId);
  const isUsingFallbackAkt = !isDigitalizedServitut
    && !searchTargets.korrektAktId
    && !searchTargets.servitutAktId
    && !searchTargets.textAktId
    && Boolean(searchTargets.ovrigAktId);
  const showDownloadButton =
    hasAccess(Feature.DownloadAktFraTinglysning) &&
    shouldRenderTingbogDownloadButton(servitut, stamOplysninger);
  const downloadTarget = showDownloadButton
    ? getTingbogDownloadTarget(servitut, stamOplysninger)
    : undefined;
  const servitutPdfActionAvailability = getTingbogPdfActionAvailability({
    downloadTarget,
    viewerAktId,
    viewerContextType: AnnotationContextType.Servitut,
    viewerDocumentId: servitut.dokumentId || servitut.aktAliasId || undefined,
    aiAnalysisContext: {
      servitut,
      stamOplysninger,
      viamapTingBog,
      propertyData,
    },
  }, hasAccess);

  const annotationQuickDownloadButton = (
    <ServitutAnnotationQuickDownloadButton
      documentId={normalizeServitutIdentity(servitut).documentId}
      datoloebenr={recordNo}
      downloadFileName={downloadFileName}
    />
  );

  if (!servitutPdfActionAvailability.hasAnyActions) {
    return annotationQuickDownloadButton;
  }

  return (
    <div className="tingbog-servitut-action">
      {annotationQuickDownloadButton}
      <TingbogPdfActions
        downloadTarget={downloadTarget}
        downloadFileName={downloadFileName}
        viewerAktId={viewerAktId}
        viewerContextType={AnnotationContextType.Servitut}
        viewerDocumentId={servitut.dokumentId || servitut.aktAliasId || undefined}
        viewerDatoloebenr={recordNo}
        viewerDocumentFilename={downloadFileName}
        aiAnalysisContext={servitutPdfActionAvailability.canOpenWithAI ? {
          servitut,
          stamOplysninger,
          viamapTingBog,
          propertyData,
        } : undefined}
        beforeButtonContent={isUsingKorrektAkt ? (
          <TooltipWrapper
            title={Localization.getText("Akt id was marked as the correct deed (KORREKT AKT).")}
            className="ViewButtonToolTip tingbog-servitut-fallback-tooltip"
          >
            <span className="tingbog-servitut-fallback-indicator tingbog-servitut-fallback-indicator--korrekt">K</span>
          </TooltipWrapper>
        ) : isUsingTextAkt ? (
          <TooltipWrapper
            title={Localization.getText("Akt id was found in the easement text.")}
            className="ViewButtonToolTip tingbog-servitut-fallback-tooltip"
          >
            <span className="tingbog-servitut-fallback-indicator tingbog-servitut-fallback-indicator--text">T</span>
          </TooltipWrapper>
        ) : isUsingFallbackAkt ? (
          <TooltipWrapper
            title={Localization.getText("Servitut akt not found. Trying property's øvrige akt.")}
            className="ViewButtonToolTip tingbog-servitut-fallback-tooltip"
          >
            <span className="tingbog-servitut-fallback-indicator">!</span>
          </TooltipWrapper>
        ) : undefined}
      />
    </div>
  );
}

export function AdkomstTingbogPdfActions(props: {
  adkomst: {
    tinglysningsDato?: string;
    dokumentId?: string;
    aktId?: string;
    aktAliasId?: string;
    dokumentFilnavn?: string;
  };
  bfeNumber?: string | number;
  stamOplysninger?: { indskannetAktDokumentId?: string };
  allowDownload?: boolean;
  hasAccess: (feature: FeatureType) => boolean;
}) {
  const { adkomst, bfeNumber, stamOplysninger, allowDownload = true, hasAccess } = props;
  if (!adkomst) {
    return null;
  }
  if (!ENABLE_TINGBOG_ADKOMST_HAEFTELSE_PDF_UI) {
    return null;
  }
  if (!isTinglysningPdfEligibleByDate(adkomst.tinglysningsDato)) {
    return null;
  }

  const isDigitalizedAdkomst = isTingbogElectronicDocument(adkomst);
  const adkomstFilnavn = adkomst.dokumentFilnavn?.trim();
  const viewerAktId = isDigitalizedAdkomst
    ? undefined
    : (adkomstFilnavn || stamOplysninger?.indskannetAktDokumentId);
  const showDownloadButton =
    allowDownload &&
    hasAccess(Feature.DownloadAktFraTinglysning) &&
    shouldRenderTingbogDownloadButton(adkomst);
  const downloadTarget = showDownloadButton
    ? getTingbogDownloadTarget(adkomst)
    : undefined;
  const adkomstPdfActionAvailability = getTingbogPdfActionAvailability({
    downloadTarget,
    viewerAktId,
    viewerContextType: AnnotationContextType.Ejendom,
    viewerBfeNumber: bfeNumber,
  }, hasAccess);

  if (!adkomstPdfActionAvailability.hasAnyActions) {
    return null;
  }

  const downloadFileName = getTingbogDownloadFileName(
    `adkomst_${TingBogRecordNumber(adkomst.aktId || adkomst.aktAliasId || "") || adkomst.dokumentId || ""}`
  );

  return (
    <div className="tingbog-servitut-action">
      <TingbogPdfActions
        downloadTarget={downloadTarget}
        downloadFileName={downloadFileName}
        viewerAktId={viewerAktId}
        viewerContextType={AnnotationContextType.Ejendom}
        viewerBfeNumber={bfeNumber}
        viewerDocumentFilename={downloadFileName}
      />
    </div>
  );
}

function ServitutRow({ servitut, hasAccess, stamOplysninger, viamapTingBog, propertyData }: any) {
  const tingbogsLink = getTingbogsDokLink(servitut);
  const recordNo = TingBogRecordNumber(servitut.aktId || servitut.aktAliasId);
  const priorityNo = servitut.prioritetNummer?.toString().padStart(2, "0") || "??";
  const servitutSummary = getServitutSummaryText(servitut);
  const downloadFileName = getServitutMainDocumentFileName(servitut);
  const recordLinkOrText = hasAccess(Feature.ServitutDetaljer)
    ? (
      <OpenInServitutView
        text={recordNo}
        uuid={servitut.dokumentId}
        servitut={servitut}
        stamOplysninger={stamOplysninger}
        viamapTingBog={viamapTingBog}
        propertyData={propertyData}
      />
    )
    : (
      (tingbogsLink && (
        <a type="application/pdf" target="_blank" rel="noopener noreferrer" href={tingbogsLink}>
          {recordNo}
        </a>
      )) || recordNo
    );

  return (
    <div className="Line-MitProp tingbog-entry-row" key={servitut.aktId || servitut.aktAliasId}>
      <div className="tingbog-entry-content">
        <div className="tingbog-servitut-main">
          <div className="tingbog-servitut-priority" title="Prioritet Nummer">
            {priorityNo}
          </div>
          <div className="tingbog-servitut-texts">
            <div className="tingbog-servitut-record-row">
              <div className="tingbog-servitut-record">{recordLinkOrText}</div>
              <ServitutAnnotationCountBadge
                documentId={normalizeServitutIdentity(servitut).documentId}
                datoloebenr={recordNo}
                downloadFileName={downloadFileName}
              />
            </div>
            <div className={`tingbog-servitut-meta ${servitut.dokumentFilnavn ? "" : "no-document-id"}`}>
              {servitut.dokumentFilnavn ? (
                <span className="tingbog-servitut-document-id">
                  {servitut.dokumentFilnavn}
                </span>
              ) : null}
              <span className="tingbog-servitut-summary">{servitutSummary}</span>
            </div>
          </div>
          <ServitutTingbogPdfActions
            servitut={servitut}
            stamOplysninger={stamOplysninger}
            viamapTingBog={viamapTingBog}
            propertyData={propertyData}
            hasAccess={hasAccess}
          />
        </div>
      </div>
    </div>
  );
}

export function TingBogRecordNumber(a: string) {
  return a?.replace(/(.{4})(.{2})(.{2})-()/, "$3$2$1-$4") ?? "";
}

export function getServitutSummaryText(servitut: {
  tekstSummarisk?: string;
  tekster?: string[];
}): string {
  return (
    servitut.tekstSummarisk ||
    (servitut.tekster && servitut.tekster.join(" ")) ||
    ""
  ).trim();
}

export function getServitutMainDocumentFileName(servitut: {
  aktId?: string;
  aktAliasId?: string;
  prioritetNummer?: string | number;
  tekstSummarisk?: string;
  tekster?: string[];
  dokumentId?: string;
  dokumentFilnavn?: string;
}): string {
  const datoloebenr = TingBogRecordNumber(servitut.aktId || servitut.aktAliasId || "");
  return getServitutDownloadFileName({
    datoloebenr,
    priorityNumber: servitut.prioritetNummer,
    headingText: getServitutSummaryText(servitut),
    fallbackStem:
      servitut.dokumentId ||
      servitut.dokumentFilnavn ||
      datoloebenr ||
      "servitut",
  });
}

export function getTingbogsDokLink(servitut: any): string | undefined {
  return getTingbogPdfUrl(servitut);
}

function BeautyString(a: unknown): string {
  if (a === null || a === undefined) {
    return "";
  }
  if (typeof a === "string") {
    return a.trim();
  }
  if (a instanceof Date) {
    return a.toLocaleDateString(Localization.getLocale());
  }
  if (typeof a === "number") {
    return new Intl.NumberFormat("da-dk").format(a);
  }
  if (typeof a === "object") {
    return " ";
  }
  return String(a);
}